// Pure rules of the /cvc-migration page. jest cannot load .vue files, so every
// decision the components make lives here and the components only render it.

import { chunk, errorMessage, errorSeverity, mergeVerifyResults } from '@/utils/cvcMigration';
import { toCsv } from '@/utils/csv';
import {
  SYDNEY,
  POI_TIMEZONE,
  ZONE_LABEL,
  TOO_SOON,
  formatDual,
  formatInZone,
  formatClockInZone,
  toDate,
  wallClockToUtc,
  windowWarnings,
} from '@/utils/zonedTime';

// Every step flag of Preflight `steps` (the server owns the rules).
export const STEP_NAMES = [
  'canSetWindow', 'canPreAssign', 'canRelease', 'canDownloadReport',
  'canBuild', 'canVerify', 'canRepolicy', 'canRebuild', 'canRemoveTargetCopies', 'canForceRemoveTargetCopy',
  'canShutdown', 'canRollback', 'canConfirmRepoint', 'canConfirmWithFailedShut',
  'canActivate', 'canCommit', 'canKick', 'canVerifyReconnected',
  'canMarkMigrated', 'canRemoveSourceCopies', 'canCancel',
  'canStartRollback', 'canEditRollbackWindow', 'canStopRollback', 'canFixSource', 'canVerifySource',
  'canConfirmRepointBack', 'canMoveBack', 'canKickRestored', 'canCloseRolledBack',
];

const NO_PREFLIGHT_REASON = 'Run Preflight first';
const NO_REASON_GIVEN = 'Not available now';

// A button is enabled only when the server says so; when it does not, the
// server's reason is shown under the button and as its tooltip.
export function stepButton (steps, name) {
  if (!steps) return { disabled: true, reason: NO_PREFLIGHT_REASON };
  if (steps[name] === true) return { disabled: false, reason: '' };
  const reason = steps.reasons && steps.reasons[name];
  return { disabled: true, reason: reason || NO_REASON_GIVEN };
}

// For the flags a server release may not send yet: an absent flag is "not
// available yet", not "off for a reason".
const NEW_FLAGS = ['canRebuild', 'canForceRemoveTargetCopy'];

export function flagButton (steps, name) {
  if (steps && NEW_FLAGS.includes(name) && steps[name] === undefined) {
    return { disabled: true, reason: (steps.reasons && steps.reasons[name]) || 'Not available yet' };
  }
  return stepButton(steps, name);
}

const FAST_REFRESH_MS = 30000;
const IDLE_REFRESH_MS = 120000;
const FAST_PHASES = [
  'shutPending', 'shutFailed', 'waitingRepoint', 'rollbackPending',
  'rollbackFailed', 'repointConfirmed', 'activated', 'rollingBack',
];

export function refreshIntervalMs (phase, pendingEvents) {
  return pendingEvents || FAST_PHASES.includes(phase) ? FAST_REFRESH_MS : IDLE_REFRESH_MS;
}

// The silent refresh waits while an action of this tab runs, while the tab is
// hidden, and until the interval has passed.
export function shouldRefreshNow ({ nowMs, lastMs, intervalMs, visible, busy, enabled }) {
  if (!enabled || !visible || busy) return false;
  return nowMs - lastMs >= intervalMs;
}

// An answer belongs to the page only while the CVC and target it was asked for
// are still the chosen ones; a late answer for another pick must be dropped.
// The reconnection check sends no target, so both sides leave it out.
export function isCurrentAnswer (requested, current) {
  return requested.cvcId === current.cvcId && requested.targetHostId === current.targetHostId;
}

// The page's three start-up calls (environment, CVC list, hosts) run together
// and each answer is handed over as it arrives: a slow or failed list call must
// not hold back the QA banner, and a failed environment call must not hide the
// lists. Resolves when all three have settled; a failed call goes to
// on.failed(name, error), it never rejects.
const STARTUP_CALLS = ['environment', 'cvcs', 'hosts'];

export async function loadStartup (calls, on) {
  await Promise.all(STARTUP_CALLS.map(name =>
    new Promise(resolve => resolve(calls[name]())).then(answer => on[name](answer), error => on.failed(name, error))
  ));
}

// Verify target runs as sequential calls of at most verifyMaxAvcs router-scope
// AVC ids (Usher gives up on a router session after 30 s). `first`/`last` are
// the 1-based positions in the router scope list, for the progress text. A CVC
// with nothing in router scope still gets one call, without ids, so the CVC
// and loop subinterfaces are checked. `onlyIds` narrows it to chosen AVCs; if
// none of them is in router scope there is nothing to call (an id-less call
// would verify the whole CVC).
export function verifyPlan (rows, verifyMaxAvcs, onlyIds) {
  const ids = (rows || [])
    .filter(r => r.inRouterScope && (!onlyIds || onlyIds.includes(r.avcId)))
    .map(r => r.avcId);
  if (ids.length === 0) return onlyIds ? [] : [{ avcIds: undefined, first: 0, last: 0 }];
  let first = 1;
  return chunk(ids, verifyMaxAvcs).map(avcIds => {
    const step = { avcIds, first, last: first + avcIds.length - 1 };
    first += avcIds.length;
    return step;
  });
}

const VERIFY_BUSY_SUFFIX = 'is running queued changes. Verify again after they finish (AVC cron).';

// Usher refuses to verify a source when it cannot tell which customer an AVC had before
// activation; the 409 names those AVCs. It is a refusal, not a busy router.
const unknownCustomerIds = (e) => {
  const body = e && e.response && e.response.status === 409 ? e.response.data : null;
  const ids = body && body.data && body.data.unknown;
  return Array.isArray(ids) && ids.length > 0 ? ids : null;
};

// Runs the plan one call at a time. Any failed call stops the loop and keeps
// what was verified so far: done | user (stop after this chunk) | busy (409,
// the router has queued changes) | refused (409 naming AVCs whose customer is
// unknown: a data fix) | error.
export async function runVerifyLoop ({ plan, callChunk, targetHostname, shouldStop, onProgress }) {
  const results = [];
  let stoppedBy = 'done';
  let message = '';
  for (let i = 0; i < plan.length; i++) {
    if (i > 0 && shouldStop && shouldStop()) {
      stoppedBy = 'user';
      break;
    }
    if (onProgress) onProgress({ index: i + 1, total: plan.length, first: plan[i].first, last: plan[i].last });
    try {
      results.push(await callChunk(plan[i].avcIds));
    } catch (e) {
      const unknown = unknownCustomerIds(e);
      if (unknown) {
        stoppedBy = 'refused';
        message = `${errorMessage(e)}: ${unknown.join(', ')}. Data fix needed (dev).`;
        break;
      }
      const busy = e && e.response && e.response.status === 409;
      stoppedBy = busy ? 'busy' : 'error';
      message = busy ? `${targetHostname} ${VERIFY_BUSY_SUFFIX}` : errorMessage(e);
      break;
    }
  }
  return { results, merged: mergeVerifyResults(results), stoppedBy, message };
}

const isVisp = (row) => row.avcClass === 'VISP';
export const inScope = (row) => isVisp(row) && row.inRouterScope;
const byAvc = (list) => (list || []).reduce((map, d) => { map[d.avcId] = d; return map; }, {});
const isPolicyOnly = (d) => (d.issues || []).length > 0 && d.issues.every(issue => /^policy/i.test(issue));
// No copy on the target: never built, or the build failed.
const lacksCopy = (row) => row.targetBuildStatus === 'none' || row.targetBuildStatus === 'failed';

// How a verified AVC differs from its target copy: ok | policy (only the speed
// policy is wrong: repolicy) | rebuild (anything else, or missing on the router).
export function driftKind (detail) {
  if (detail.match) return 'ok';
  return detail.present !== false && isPolicyOnly(detail) ? 'policy' : 'rebuild';
}

// VISP router-scope AVCs with no target copy and nothing queued for them: the
// ids behind "Build n missing".
export function missingBuildIds (rows) {
  return (rows || [])
    .filter(r => inScope(r) && lacksCopy(r) && !r.pendingEvent)
    .map(r => r.avcId);
}

// What "Fix drift" would do, in the order it runs: build (no copy), rebuild
// (the copy is missing or wrong on the router), repolicy (only the speed policy
// is wrong), remove (orphan copies). An AVC with a queued change is left alone.
export function planDriftFixes (rows, lastVerify, record) {
  const details = byAvc(lastVerify && lastVerify.details);
  const build = [];
  const rebuild = [];
  const repolicy = [];
  (rows || []).filter(r => inScope(r) && !r.pendingEvent).forEach(row => {
    const d = details[row.avcId];
    if (lacksCopy(row)) build.push(row.avcId);
    else if (d && driftKind(d) === 'policy') repolicy.push(row.avcId);
    else if (d && driftKind(d) === 'rebuild') rebuild.push(row.avcId);
  });
  const remove = [];
  const addCopy = (id) => { if (id !== undefined && id !== null && !remove.includes(id)) remove.push(id); };
  ((record && record.targetCopies) || []).filter(c => c.orphan && !c.removedAt).forEach(c => addCopy(c.id));
  ((lastVerify && lastVerify.unexpected) || []).forEach(u => addCopy(u.copyId));
  const lines = [];
  if (build.length) lines.push(`Build ${build.join(', ')}`);
  if (rebuild.length) lines.push(`Rebuild ${rebuild.join(', ')}`);
  if (repolicy.length) lines.push(`Repolicy ${repolicy.join(', ')}`);
  if (remove.length) lines.push(`Remove copies ${remove.join(', ')}`);
  return {
    build,
    rebuild,
    repolicy,
    remove,
    count: build.length + rebuild.length + repolicy.length + remove.length,
    lines,
  };
}

// The "Remove orphan copies" button of Section B. On a cancelled record the
// server lets it remove every target copy (none is wanted any more), and an
// empty id list means "all" there, so the ids are always sent and counted.
export function orphanRemoval (record, plan, target) {
  const cancelled = !!(record && record.cancelledAt);
  const live = ((record && record.targetCopies) || []).filter(c => !c.removedAt).map(c => c.id);
  const ids = cancelled ? live.concat(plan.remove.filter(id => !live.includes(id))) : plan.remove;
  const what = `${ids.length} ${cancelled ? 'target' : 'orphan'} copies`;
  return {
    ids,
    label: `Remove ${what}`,
    question: `Remove ${what} from ${target}?`,
    queued: `Removal of ${what} queued. Wait for the queued changes, then Verify target.`,
  };
}

// "3 of 4 built . 2 verified ready . 1 need verify . 1 orphans"
export function copiesSummary (record, rows) {
  const scopeRows = (rows || []).filter(inScope);
  const inScopeIds = scopeRows.map(r => r.avcId);
  const copies = ((record && record.targetCopies) || []).filter(c => !c.removedAt);
  const built = scopeRows.filter(r => r.targetBuildStatus === 'complete').length;
  const ready = copies.filter(c => c.ready && inScopeIds.includes(c.avcId)).length;
  const needVerify = Math.max(0, built - ready);
  const orphans = copies.filter(c => c.orphan).length;
  return {
    scope: scopeRows.length,
    built,
    ready,
    needVerify,
    orphans,
    text: `${built} of ${scopeRows.length} built . ${ready} verified ready . ${needVerify} need verify . ${orphans} orphans`,
  };
}

const ACTIVATED_PHASES = ['activated', 'migrated', 'removed'];
const NOT_ON_OUR_RADIUS = ['notOnOurRadius', 'noRadiusService'];
// A suspended service has a RADIUS service but no session is expected: never offline.
const SUSPENDED_ADVICE = 'No session expected (suspended)';
const isSuspended = (reconnectRow) => !!reconnectRow && reconnectRow.reason === 'suspended';

// The one status word per grid row. First rule that applies wins:
// out of scope, L2, LOCAL VLAN conflict, reconnection (after the kick, and
// before the shut only for a service live on the target), build failed, verify
// mismatch, build queued, no copy, copy not verified, ready.
// `othersBuilt` marks a missing copy as "new" (an order that arrived after the
// build).
export function rowBadge (row, verifyDetail, reconnectRow, phase, { othersBuilt = false, record, poiState } = {}) {
  const reconnect = reconnectRow && (!BEFORE_SHUT_PHASES.includes(phase) || liveOnTarget(reconnectRow)) ? reconnectRow : null;
  const badge = (label, tone, reason = '') => ({ label, tone, reason });
  if (!row.inRouterScope) return badge('Not in scope', 'grey', 'Not in router scope (not active and provisioned in Usher)');
  if (!isVisp(row)) return badge('L2 blocked', 'grey', 'L2 keeps its own bridge domain; L2 services are migrated in a later release');
  if (row.localVlanConflictAvcId) return badge('VLAN conflict', 'red', `Held in LOCAL network by ${row.localVlanConflictAvcId}`);
  if (isSuspended(reconnect)) return badge('Suspended', 'grey', SUSPENDED_ADVICE);
  if (reconnect && !NOT_ON_OUR_RADIUS.includes(reconnect.reason)) {
    return reconnect.reconnected
      ? badge('Reconnected', 'green')
      : badge('Offline', 'red', reconnectVerdict(reconnect, { record, poiState }).text);
  }
  if (row.targetBuildStatus === 'failed') return badge('Build failed', 'red', row.targetBuildError || '');
  if (verifyDetail && verifyDetail.match === false) {
    return badge('Mismatch', 'red', (verifyDetail.issues || [])[0] || '');
  }
  if (row.targetBuildStatus === 'pending') return badge('Building', 'blue', 'Build queued in Usher');
  if (row.targetBuildStatus === 'none') {
    return othersBuilt
      ? badge('Not built (new)', 'amber', 'No copy on the target; the other AVCs are built')
      : badge('Not built', 'amber', 'No copy on the target yet');
  }
  if (!row.targetCopyReady) return badge('Needs verify', 'amber', 'Built, not verified since the last change');
  return ACTIVATED_PHASES.includes(phase) ? badge('Swapped', 'green') : badge('Ready', 'green');
}

// "14/10 10:00 SYD . 07:00 PER to 14/10 11:00 SYD . 08:00 PER"
export function windowRangeText (startUtc, endUtc, poiState) {
  if (!toDate(startUtc) || !toDate(endUtc)) return '';
  return `${formatDual(startUtc, poiState)} to ${formatDual(endUtc, poiState)}`;
}

const EVENT_LABEL = {
  'shutdown-cvc': 'Shut source',
  'no-shutdown-cvc': 'Roll back source shut',
  'remove-copies': 'Remove copies',
};

const MINUTE_MS = 60 * 1000;

// Statics grouped by the time and the operator of one stamp (`committedAt` or
// `restoredAt`): one run each.
function ledgerRuns (statics, stampKey, whoKey) {
  const runs = {};
  (statics || []).filter(s => s[stampKey]).forEach(s => {
    const id = `${s[stampKey]}|${s[whoKey] || ''}`;
    runs[id] = runs[id] || { at: s[stampKey], by: s[whoKey], count: 0 };
    runs[id].count++;
  });
  return Object.keys(runs).map(id => runs[id]);
}

// The rollback after the re-point confirm, one row per stamp of the record. A window
// change is not a row; a second start replaces the first (the audit log keeps both).
function rollbackRows (record, poiState) {
  const rows = [];
  if (record.rollbackStartedAt) {
    const window = record.rollbackWindowStart && record.rollbackWindowEnd
      ? windowShortText(record.rollbackWindowStart, record.rollbackWindowEnd, poiState)
      : 'not booked yet';
    rows.push({
      time: record.rollbackStartedAt,
      who: record.rollbackStartedBy,
      what: 'Rollback started',
      result: `${record.rollbackReason}. Agreed by ${record.rollbackAgreedBy}. Optus window: ${window}`,
    });
  }
  [
    ['rollbackStopped', 'Rollback stopped'],
    ['repointBackConfirmed', 'Re-point back confirmed'],
    ['movedBack', 'Moved back in Usher'],
    ['rolledBack', 'Closed as rolled back'],
  ].forEach(([key, what]) => {
    if (record[`${key}At`]) rows.push({ time: record[`${key}At`], who: record[`${key}By`], what, result: '' });
  });
  return rows;
}

// "Activity" table: who did what, newest first, built from the migration record
// the server returns and, when given, the statics ledger. With `nowUtc`, a pending event says how long it
// has waited (and the stalled cron hint once it has waited too long). The
// server keeps no time for the window itself, so that row has no time and sits
// just above "Record created".
export function activityRows (record, poiState, nowUtc, statics) {
  if (!record) return [];
  const rows = [];
  const add = (time, who, what, result, sortMs, kind = 'record') => {
    const date = toDate(time);
    rows.push({
      sortMs: sortMs !== undefined ? sortMs : date ? date.getTime() : 0,
      timeText: date ? formatDual(date, poiState) : '-',
      who: who || '',
      what,
      result: result || '',
      kind,
    });
  };
  const created = toDate(record.createdAt);
  add(record.createdAt, record.createdBy, 'Record created');
  if (record.cutoverWindowStart && record.cutoverWindowEnd) {
    const window = windowRangeText(record.cutoverWindowStart, record.cutoverWindowEnd, poiState);
    add(null, record.windowSetBy, 'Window set', window, created ? created.getTime() + 1 : 0);
  }
  if (record.repointConfirmedAt) {
    add(record.repointConfirmedAt, record.repointConfirmedBy, 'Re-point confirmed',
      record.repointWithoutShut ? 'Source shut failed (override)' : '');
  }
  if (record.activatedAt) add(record.activatedAt, record.activatedBy, 'Activated');
  // The Usher ledger names each run (statics committed or restored together); the
  // record stamp is the older, single "IPs committed" row.
  const commitRuns = ledgerRuns(statics, 'committedAt', 'committedBy');
  if (record.ipsCommittedAt && !commitRuns.length) add(record.ipsCommittedAt, record.ipsCommittedBy, 'IPs committed');
  commitRuns.forEach(run => add(run.at, run.by, 'Statics committed', staticsText(run.count), undefined, 'commit'));
  ledgerRuns(statics, 'restoredAt', 'restoredBy')
    .forEach(run => add(run.at, run.by, 'Old IPs restored', staticsText(run.count), undefined, 'rollback'));
  if (record.kickedAt) add(record.kickedAt, record.kickedBy, 'Kick all');
  if (record.completedAt) add(record.completedAt, record.completedBy, 'Marked migrated');
  // A record closed as rolled back is also stamped cancelled: only its own row is shown.
  if (record.cancelledAt && !record.rolledBackAt) add(record.cancelledAt, record.cancelledBy, 'Cancelled', '', undefined, 'cancel');
  rollbackRows(record, poiState).forEach(r => add(r.time, r.who, r.what, r.result, undefined, 'rollback'));
  (record.events || []).forEach(ev => {
    const status = String(ev.eventStatus || '').toLowerCase();
    const queued = nowUtc ? queuedEventStatus({ status, createdAt: ev.createdAt }, 'cvc', nowUtc) : null;
    let result = ev.error ? `${status}: ${ev.error}` : status;
    if (queued && queued.pending) result = queued.hint ? `${queued.text}. ${queued.hint}` : queued.text;
    add(ev.createdAt, ev.createdBy, `${EVENT_LABEL[ev.eventType] || ev.eventType} (event ${ev.id})`, result,
      undefined, ev.eventType === 'no-shutdown-cvc' ? 'rollback' : 'record');
  });
  [['sourceCopies', 'source copies'], ['targetCopies', 'target copies']].forEach(([key, label]) => {
    const groups = {};
    (record[key] || []).filter(c => c.removeRequestedAt).forEach(c => {
      const id = `${c.removeRequestedAt}|${c.removeRequestedBy || ''}`;
      groups[id] = groups[id] || { at: c.removeRequestedAt, by: c.removeRequestedBy, total: 0, removed: 0 };
      groups[id].total++;
      if (c.removedAt) groups[id].removed++;
    });
    Object.keys(groups).forEach(id => {
      const g = groups[id];
      add(g.at, g.by, `Remove ${label} requested (${g.total} ${g.total === 1 ? 'copy' : 'copies'})`,
        `${g.removed} of ${g.total} removed`);
    });
  });
  return rows
    .sort((a, b) => b.sortMs - a.sortMs)
    .map(r => ({ timeText: r.timeText, who: r.who, what: r.what, result: r.result, kind: r.kind }));
}

// "Updated by another session": the record changed and this tab did not do it.
export function changedElsewhere (previousUpdatedAt, currentUpdatedAt, ownAction) {
  const before = toDate(previousUpdatedAt);
  const now = toDate(currentUpdatedAt);
  if (ownAction || !before || !now) return false;
  return before.getTime() !== now.getTime();
}

// How long a queued Usher change has waited. The crons run every few minutes,
// so a change waiting longer than this points at the cron on the Usher host.
const STALE_AFTER_MINUTES = { avc: 10, cvc: 15 };

export function queuedEventStatus (event, kind, nowUtc) {
  const none = { pending: false, minutes: 0, text: '', stale: false, hint: '' };
  if (!event || String(event.status).toLowerCase() !== 'pending') return none;
  const queued = toDate(event.createdAt);
  const now = toDate(nowUtc);
  if (!queued || !now) return { pending: true, minutes: 0, text: 'queued', stale: false, hint: '' };
  const minutes = Math.max(0, Math.floor((now - queued) / MINUTE_MS));
  const stale = minutes >= STALE_AFTER_MINUTES[kind];
  return {
    pending: true,
    minutes,
    text: minutes === 0 ? 'queued just now' : `queued ${minutes} min ago`,
    stale,
    hint: stale ? `No progress from the Usher cron for ${minutes} min. Check the Usher host (.27) cron.` : '',
  };
}

// The operator must type the CVC name (exactly, spaces around it ignored) before
// a destructive step is enabled.
export function requiresTypedName (text, cvcName) {
  return !!cvcName && typeof text === 'string' && text.trim() === cvcName;
}

// "Name (CVC000000781477) . WA . host"; `migrated` adds the chip.
export function pickerItems (cvcs, hosts) {
  const hostNames = (hosts || []).reduce((map, h) => { map[h.id] = h.hostname; return map; }, {});
  return (cvcs || []).map(c => {
    const parts = [`${c.cvcName || c.cvcId} (${c.cvcId})`];
    if (c.poiState) parts.push(c.poiState);
    if (hostNames[c.networkHostId]) parts.push(hostNames[c.networkHostId]);
    return { id: c.id, label: parts.join(' . '), migrated: !!c.localTermination };
  });
}

export function hostItems (hosts) {
  return (hosts || []).map(h => ({
    id: h.id,
    label: `${h.hostname}${h.description ? ` (${h.description})` : ''} - ${h.ipAddress}`,
  }));
}

// Index of the section (A..E, R) that belongs to the phase: it opens on load and
// when the phase moves on.
export const CUTOVER_SECTION = 2;
export const ROLLBACK_SECTION = 5;

const SECTION_OF_PHASE = {
  none: 0,
  planning: 0,
  prebuilding: 1,
  ready: 1,
  shutPending: CUTOVER_SECTION,
  shutFailed: CUTOVER_SECTION,
  waitingRepoint: CUTOVER_SECTION,
  rollbackPending: ROLLBACK_SECTION,
  rollbackFailed: ROLLBACK_SECTION,
  rollingBack: ROLLBACK_SECTION,
  rolledBackClosed: ROLLBACK_SECTION,
  repointConfirmed: CUTOVER_SECTION,
  activated: 3,
  migrated: 4,
  removed: 4,
  cancelled: 4,
};

export function openSection (phase) {
  return SECTION_OF_PHASE[phase] || 0;
}

// The open panels with one more section in them (once).
export function addSection (panels, index) {
  const open = panels || [];
  return open.includes(index) ? open : open.concat(index);
}

const PHASES = {
  none: { label: 'Not started', color: 'grey', meaning: 'No migration record yet', step: -1 },
  planning: { label: 'Planning', color: 'blue', meaning: 'Record open, nothing built on the target yet', step: 0 },
  prebuilding: { label: 'Pre-building', color: 'blue', meaning: 'Target copies exist but are not all verified, or changes are queued', step: 1 },
  ready: { label: 'Ready for cutover', color: 'green', meaning: 'Every target copy is verified', step: 2 },
  shutPending: { label: 'Shutting source', color: 'amber', meaning: 'The source shut is queued', step: 3 },
  shutFailed: { label: 'Source shut failed', color: 'red', meaning: 'The source shut failed and the re-point is not confirmed', step: 3 },
  waitingRepoint: { label: 'Waiting for Optus', color: 'amber', meaning: 'The source is shut; waiting for Optus to re-point the CVC', step: 4 },
  rollbackPending: { label: 'Rolling back source', color: 'amber', meaning: 'Bringing the source subinterfaces back up', step: 3 },
  rollbackFailed: { label: 'Rollback failed', color: 'red', meaning: 'The source rollback failed', step: 3 },
  repointConfirmed: { label: 'Re-point confirmed', color: 'green', meaning: 'Optus re-pointed; not activated yet', step: 4 },
  activated: { label: 'Activated', color: 'green', meaning: 'Usher points the CVC at the target', step: 5 },
  migrated: { label: 'Migrated (source shut)', color: 'green', meaning: 'Marked migrated; the source copies are still there, shut', step: 6 },
  removed: { label: 'Source copies removed', color: 'grey', meaning: 'Migration finished; the source copies are gone', step: 7 },
  cancelled: { label: 'Cancelled', color: 'grey', meaning: 'The migration was cancelled', step: -1 },
  rollingBack: { label: 'Rolling back: waiting for Optus', color: 'amber', meaning: 'A rollback to the source was started; waiting for Optus to re-point back', step: 4 },
  rolledBackClosed: { label: 'Rolled back', color: 'grey', meaning: 'The migration was rolled back; customers are on the source', step: -1 },
};

export function phaseInfo (phase) {
  return PHASES[phase] || PHASES.none;
}

const STRIP_LABELS = ['Plan', 'Pre-build', 'Ready', 'Shut', 'Re-point', 'Activated', 'Done', 'Removed'];

// The eight chips under the header: steps before the current one are done, the
// current one takes the phase colour. Nothing is current without a record or
// after a cancel.
export function phaseStrip (phase) {
  const info = phaseInfo(phase);
  return STRIP_LABELS.map((label, i) => ({
    label,
    state: i < info.step ? 'done' : i === info.step ? 'current' : 'todo',
    color: i === info.step ? info.color : 'grey',
  }));
}

// ---- Section A: the cutover window (entered in Sydney time) ----

const pad2 = (n) => String(n).padStart(2, '0');

export const WINDOW_STARTS = (() => {
  const times = [];
  for (let minutes = 6 * 60; minutes <= 20 * 60; minutes += 15) {
    times.push(`${pad2(Math.floor(minutes / 60))}:${pad2(minutes % 60)}`);
  }
  return times;
})();
export const WINDOW_DURATIONS = [30, 60, 90, 120];
export const DEFAULT_WINDOW_DURATION = 60;

// The form holds a Sydney date + time; the server stores UTC and never converts.
export function windowFromForm (form) {
  const start = form && wallClockToUtc({ date: form.date, time: form.start }, SYDNEY);
  if (!start) return null;
  const end = new Date(start.getTime() + (form.duration || DEFAULT_WINDOW_DURATION) * MINUTE_MS);
  return { startUtc: start.toISOString(), endUtc: end.toISOString() };
}

export function windowToForm (startUtc, endUtc) {
  const start = toDate(startUtc);
  const end = toDate(endUtc);
  if (!start || !end) return null;
  const minutes = Math.round((end - start) / MINUTE_MS);
  return {
    date: sydneyDate(start),
    start: clockOf(start, SYDNEY),
    duration: WINDOW_DURATIONS.includes(minutes) ? minutes : DEFAULT_WINDOW_DURATION,
  };
}

// "2026-10-14": today's date in Sydney, for the date picker.
export function sydneyDate (utc) {
  const date = toDate(utc);
  if (!date) return '';
  const [dd, mm, yyyy] = formatInZone(date, SYDNEY, { year: true }).split(' ')[0].split('/');
  return `${yyyy}-${mm}-${dd}`;
}

const clockOf = (utc, zone) => formatInZone(utc, zone).split(' ')[1];
const rangeIn = (startUtc, endUtc, zone) => `${clockOf(startUtc, zone)}-${clockOf(endUtc, zone)}`;
const zoneName = (zone) => zone.split('/').pop().replace('_', ' ');

// "= Perth 07:00-08:00 (WA local) . your time 09:00-10:00 (Brisbane)". The
// operator's own zone is shown only when it shows other times than the two above.
export function windowSummary (startUtc, endUtc, poiState, browserZone) {
  if (!toDate(startUtc) || !toDate(endUtc)) return '';
  const parts = [];
  const poiZone = POI_TIMEZONE[poiState];
  const shown = [rangeIn(startUtc, endUtc, SYDNEY)];
  if (poiZone && poiZone !== SYDNEY) {
    parts.push(`${zoneName(poiZone)} ${rangeIn(startUtc, endUtc, poiZone)} (${poiState} local)`);
    shown.push(rangeIn(startUtc, endUtc, poiZone));
  }
  if (browserZone && !shown.includes(rangeIn(startUtc, endUtc, browserZone))) {
    parts.push(`your time ${rangeIn(startUtc, endUtc, browserZone)} (${zoneName(browserZone)})`);
  }
  return parts.length ? `= ${parts.join(' . ')}` : '';
}

// "14/10 10:00-11:00 SYD (07:00-08:00 PER)"
export function windowShortText (startUtc, endUtc, poiState) {
  const day = formatInZone(startUtc, SYDNEY).split(' ')[0];
  const poiZone = POI_TIMEZONE[poiState];
  const poi = poiZone && poiZone !== SYDNEY ? ` (${rangeIn(startUtc, endUtc, poiZone)} ${ZONE_LABEL[poiZone]})` : '';
  return `${day} ${rangeIn(startUtc, endUtc, SYDNEY)} SYD${poi}`;
}

export function windowSavedText (startUtc, endUtc, poiState) {
  return `Window saved: ${windowShortText(startUtc, endUtc, poiState)}`;
}

// ---- Static IPs and the pool ----

const sum = (counts) => ((counts && counts.pppoe) || 0) + ((counts && counts.ipoe) || 0);
export const reservedRows = (preflight, protocol) => ((preflight && preflight.rows) || [])
  .filter(r => r.ipType === 'Static' && r.hasPendingIp && !r.committed && (!protocol || r.protocol === protocol))
  .length;

export function staticsSummary (preflight) {
  const statics = sum(preflight && preflight.statics);
  const reserved = reservedRows(preflight);
  const without = sum(preflight && preflight.staticsWithoutPendingIp);
  const dynamics = sum(preflight && preflight.dynamics);
  return {
    statics,
    reserved,
    without,
    dynamics,
    text: `${statics} statics . ${reserved} reserved . ${without} without IP . ${dynamics} dynamic (no IP step)`,
  };
}

export function poolSummary (preflight) {
  const pool = (preflight && preflight.pool) || {};
  const needed = (preflight && preflight.staticsWithoutPendingIp) || {};
  const line = (name, available, need, reserved) => `${name} available ${available || 0} . needed ${need || 0} . reserved ${reserved}`;
  return {
    pppoe: line('PPPoE', pool.pppoeAvailable, needed.pppoe, reservedRows(preflight, 'PPPoE')),
    ipoe: line('IPoE', pool.ipoeAvailable, needed.ipoe, reservedRows(preflight, 'IPoE')),
    pppoeShort: (pool.pppoeAvailable || 0) < (needed.pppoe || 0),
    ipoeShort: (pool.ipoeAvailable || 0) < (needed.ipoe || 0),
  };
}

// ---- Readiness text ----

// The server names the VISP admin page in its pool text. The link href comes
// only from the configured admin url, never from the server text.
const IP_ADDRESS_REFERENCE = 'VISP /ip-address';

export function readinessSegments (text, adminUrl) {
  const value = text == null ? '' : String(text);
  if (!adminUrl || !value.includes(IP_ADDRESS_REFERENCE)) return [{ text: value }];
  const link = {
    text: IP_ADDRESS_REFERENCE,
    href: `${String(adminUrl).replace(/\/+$/, '')}/ip-address`,
    target: '_blank',
    rel: 'noopener',
  };
  return value.split(IP_ADDRESS_REFERENCE).reduce((segments, part, i) => {
    if (i > 0) segments.push({ ...link });
    if (part) segments.push({ text: part });
    return segments;
  }, []);
}

// ---- Readiness checklist ----

const PRE_CUT_PHASES = ['none', 'planning', 'prebuilding', 'ready'];
const BEFORE_SHUT_PHASES = [...PRE_CUT_PHASES, 'shutPending'];

function subifItem (key, name, verifyPart, record, target) {
  if (verifyPart && verifyPart.interface) {
    return verifyPart.present
      ? { key, state: 'ok', label: `${name} ${verifyPart.interface} present on ${target}` }
      : { key, state: 'fail', label: `${name} ${verifyPart.interface} missing on ${target}` };
  }
  if (record && record.cvcSubifsOkAt) return { key, state: 'ok', label: `${name} present on ${target}` };
  return { key, state: 'unknown', label: `${name} - not checked yet` };
}

// state: ok | fail | warn | unknown | manual. The window check and the two
// manual reminders matter only before the cutover.
export function readinessChecklist (preflight, lastVerify, nowUtc) {
  const p = preflight || {};
  const target = p.targetHostname;
  const net = p.localNetwork;
  const items = [
    net
      ? { key: 'local-network', state: 'ok', label: `LOCAL network ${net.bridgeDomain} / outer ${net.outerVlan}` }
      : { key: 'local-network', state: 'fail', label: 'LOCAL network' },
    p.expectedLocalPopHostId && p.expectedLocalPopHostId === p.targetNetworkHostId
      ? { key: 'local-pop', state: 'ok', label: `Local POP config (${target})` }
      : { key: 'local-pop', state: 'fail', label: 'Local POP config' },
    subifItem('cvc-subif', 'CVC subif', lastVerify && lastVerify.cvcInterface, p.record, target),
    subifItem('loop-subif', 'Loop subif', lastVerify && lastVerify.loopInterface, p.record, target),
  ];
  if (!PRE_CUT_PHASES.includes(p.steps && p.steps.phase)) return items;
  const record = p.record;
  const label = 'Window at least 3 business days ahead';
  if (record && record.cutoverWindowStart && record.cutoverWindowEnd) {
    const tooSoon = windowWarnings(record.cutoverWindowStart, record.cutoverWindowEnd, nowUtc).includes(TOO_SOON);
    items.push({ key: 'window', state: tooSoon ? 'warn' : 'ok', label });
  } else {
    items.push({ key: 'window', state: 'unknown', label: `${label} (no window set)` });
  }
  items.push({ key: 'ip-report', state: 'manual', label: 'IP report sent to the wholesalers (manual)' });
  items.push({ key: 'optus', state: 'manual', label: 'Optus booking confirmed (manual)' });
  return items;
}

// ---- Batch results, errors, refresh ----

const OUTCOME_ORDER = { failed: 0, skipped: 1, done: 2 };
const outcomeRank = (outcome) => (OUTCOME_ORDER[outcome] === undefined ? 3 : OUTCOME_ORDER[outcome]);

// "Pre-assign 2 done, 1 skipped, 1 failed (10/10 15:04 SYD . 12:04 PER)"; the
// rows list failures first.
export function batchSummary (label, batch, nowUtc, poiState) {
  const b = batch || {};
  const done = b.done || 0;
  const skipped = b.skipped || 0;
  const failed = b.failed || 0;
  const rows = (b.results || [])
    .map(r => ({ avcId: r.avcId, outcome: r.outcome, message: r.message }))
    .sort((x, y) => outcomeRank(x.outcome) - outcomeRank(y.outcome));
  return {
    type: failed > 0 ? 'error' : skipped > 0 ? 'warning' : 'success',
    text: `${label} ${done} done, ${skipped} skipped, ${failed} failed (${formatDual(nowUtc, poiState)})`,
    rows,
  };
}

// The inline alert under an action: a 409 is a refusal (amber), the rest errors.
// Fix drift runs several calls in a row and tags the error with the one that failed.
export function actionAlert (e) {
  const text = errorMessage(e);
  const stopped = e && e.stage ? `Fix drift stopped at ${e.stage}` : '';
  if (errorSeverity(e) === 'warning') {
    return { type: 'warning', text: `${stopped ? `${stopped}. ` : ''}Refused: ${text}` };
  }
  return { type: 'error', text: stopped ? `${stopped}: ${text}` : text };
}

export function refreshStatus ({ lastRefreshAt, staleSince, staleMessage, intervalMs }) {
  if (staleSince) {
    return { stale: true, text: `Stale since ${clockOf(staleSince, SYDNEY)} SYD - ${staleMessage}` };
  }
  if (!lastRefreshAt) return { stale: false, text: '' };
  return {
    stale: false,
    text: `Last refresh ${formatClockInZone(lastRefreshAt, SYDNEY)} SYD (auto ${intervalMs / 1000} s)`,
  };
}

// ---- AVC grid ----

const indexBy = (list, key) => (list || []).reduce((map, item) => { map[item[key]] = item; return map; }, {});

// What the grid needs once per refresh: verify details and reconnection rows by
// AVC, and whether any VISP AVC already has a complete copy (a missing copy is
// then "new": an order that arrived after the build).
export function gridContext (rows, lastVerify, reconnectRows, phase, { kickedAt, nowUtc, record, poiState } = {}) {
  return {
    verifyByAvc: indexBy(lastVerify && lastVerify.details, 'avcId'),
    reconnectByAvc: indexBy(reconnectRows, 'avcId'),
    othersBuilt: (rows || []).some(r => inScope(r) && r.targetBuildStatus === 'complete'),
    phase,
    minutesSinceKick: minutesSince(kickedAt, nowUtc),
    record,
    poiState,
  };
}

export function badgeFor (row, ctx) {
  return rowBadge(row, ctx.verifyByAvc[row.avcId], ctx.reconnectByAvc[row.avcId], ctx.phase, {
    othersBuilt: ctx.othersBuilt,
    record: ctx.record,
    poiState: ctx.poiState,
  });
}

export const GRID_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'needsAction', label: 'Needs action' },
  { key: 'visp', label: 'VISP' },
  { key: 'l2', label: 'L2' },
  { key: 'statics', label: 'Statics' },
  { key: 'offline', label: 'Offline' },
  { key: 'notInScope', label: 'Not in scope' },
];

export function rowMatchesFilter (row, key, ctx) {
  const badge = badgeFor(row, ctx);
  switch (key) {
    case 'needsAction': return badge.tone === 'red' || badge.tone === 'amber';
    case 'visp': return isVisp(row);
    case 'l2': return !isVisp(row);
    case 'statics': return row.ipType === 'Static';
    case 'offline': return badge.label === 'Offline';
    case 'notInScope': return !row.inRouterScope;
    default: return true;
  }
}

export function filterCounts (rows, ctx) {
  return GRID_FILTERS.reduce((counts, f) => {
    counts[f.key] = (rows || []).filter(r => rowMatchesFilter(r, f.key, ctx)).length;
    return counts;
  }, {});
}

export const TONE_COLOR = {
  green: '#2e7d32',
  amber: '#ef6c00',
  blue: '#1565c0',
  red: '#c62828',
  grey: '#757575',
};

// Red rows get a pink tint, out of scope rows a grey one. A service that is
// offline is given 15 minutes after the kick to redial before its row is tinted.
const OFFLINE_TINT_AFTER_MIN = 15;

export function rowTint (badge, minutesSinceKick) {
  if (badge.label === 'Offline' && !(minutesSinceKick > OFFLINE_TINT_AFTER_MIN)) return null;
  if (badge.tone === 'red') return { backgroundColor: '#fff3f3' };
  if (badge.label === 'Not in scope') return { backgroundColor: '#f5f5f5' };
  return null;
}

export function wholesalerText (row, wholesalerNames) {
  if (!isVisp(row)) return row.customerName || '';
  const match = (wholesalerNames || []).find(w => w.accountNumber === row.accountNumber);
  return match ? match.name : String(row.accountNumber);
}

export function protocolText (row) {
  if (row.protocol && row.ipType) return `${row.protocol} ${row.ipType.toLowerCase()}`;
  return isVisp(row) ? 'no RADIUS service' : '-';
}

export function newIpText (row) {
  if (row.ipType === 'Dynamic') return 'pool';
  if (row.ipType !== 'Static') return '';
  if (row.committed) return row.pendingIp ? `${row.pendingIp} (committed)` : 'committed';
  return row.pendingIp || '';
}

export function targetCopyText (row, copy, nowUtc) {
  switch (row.targetBuildStatus) {
    case 'pending':
      return queuedEventStatus({ status: 'pending', createdAt: row.targetBuildUpdatedAt }, 'avc', nowUtc).text;
    case 'failed':
      return 'failed';
    case 'complete':
      return copy && copy.verifiedOkAt
        ? `complete, verified ${formatInZone(copy.verifiedOkAt, SYDNEY)} SYD`
        : 'complete, not verified';
    default:
      return 'none';
  }
}

export function targetCopyTip (row, nowUtc) {
  if (row.targetBuildStatus === 'pending') {
    return queuedEventStatus({ status: 'pending', createdAt: row.targetBuildUpdatedAt }, 'avc', nowUtc).hint;
  }
  return row.targetBuildStatus === 'failed' ? row.targetBuildError || '' : '';
}

export function lastCheckText (detail) {
  if (!detail) return 'not checked';
  if (detail.match) return 'match';
  return (detail.issues || [])[0] || 'mismatch';
}

export function pendingEventText (row) {
  if (!row.pendingEvent) return '';
  if (row.pendingEventType === 'patch') return 'speed change queued';
  return row.pendingEventType ? `${row.pendingEventType} queued` : 'change queued';
}

export function reconnectedText (reconnectRow) {
  if (!reconnectRow) return '-';
  if (NOT_ON_OUR_RADIUS.includes(reconnectRow.reason)) return 'n.a.';
  if (isSuspended(reconnectRow)) return 'suspended';
  if (!reconnectRow.reconnected) return 'no';
  const when = reconnectRow.lastSessionStart ? ` ${formatInZone(reconnectRow.lastSessionStart, SYDNEY)} SYD` : '';
  return `yes${when}${reconnectRow.lastNasIp ? ` ${reconnectRow.lastNasIp}` : ''}`;
}

export function usherStatusText (row) {
  return `${row.serviceStatus} . ${row.provisioningStatus}`;
}

export function emptyGridText (targetHostname) {
  return `This CVC has no AVCs in Usher. The migration moves only the CVC pointer; new orders will build on ${targetHostname}.`;
}

// ---- Section B: fix drift, row actions, typed confirm ----

// Fix drift needs every step its plan uses. Rebuilding an AVC that already has
// a copy has its own server flag, because `canBuild` is true only while some
// AVC has no copy at all.
export function driftButton (plan, steps) {
  if (!steps) return { disabled: true, reason: NO_PREFLIGHT_REASON };
  if (plan.count === 0) return { disabled: true, reason: 'Nothing to fix' };
  const needed = [];
  if (plan.build.length) needed.push('canBuild');
  if (plan.rebuild.length) needed.push('canRebuild');
  if (plan.repolicy.length) needed.push('canRepolicy');
  if (plan.remove.length) needed.push('canRemoveTargetCopies');
  for (const name of needed) {
    const button = flagButton(steps, name);
    if (button.disabled) return button;
  }
  return { disabled: false, reason: '' };
}

// The Repolicy / Rebuild button next to one verify mismatch.
export function fixButton (steps, detail) {
  return driftKind(detail) === 'policy' ? stepButton(steps, 'canRepolicy') : flagButton(steps, 'canRebuild');
}

// A dialog description from the helpers, cut down to what the confirm component takes.
const DIALOG_PROPS = ['title', 'body', 'consequences', 'alerts', 'extra', 'checkbox', 'proceedLabel', 'requireTyping'];

export function dialogProps (dialog) {
  return DIALOG_PROPS.reduce((props, key) => {
    if (dialog[key] !== undefined) props[key] = dialog[key];
    return props;
  }, {});
}

// The dialog's proceed button: the CVC name typed exactly (unless the dialog
// does not ask for it), the required tick set, and the extra field (confirmed
// by, agreed by) filled when required and within its length.
export function canProceedTyped ({ typed, cvcName, requireTyping, extra, extraValue, checkbox, checked }) {
  if (requireTyping !== false && !requiresTypedName(typed, cvcName)) return false;
  if (checkbox && checkbox.shown && checkbox.required && !checked) return false;
  if (!extra) return true;
  const value = (extraValue || '').trim();
  if (extra.required && !value) return false;
  return !(extra.maxlength && value.length > extra.maxlength);
}

// A verify of some AVCs (the row menu) updates what the page knows about those
// AVCs only; counts are re-derived from the merged details.
export function mergeVerifyInto (previous, next) {
  if (!previous) return next;
  const fresh = indexBy(next.details, 'avcId');
  const kept = (previous.details || []).map(d => fresh[d.avcId] || d);
  const added = (next.details || []).filter(d => !(previous.details || []).some(p => p.avcId === d.avcId));
  const details = kept.concat(added);
  const present = details.filter(d => d.present !== false);
  return Object.assign({}, previous, next, {
    details,
    unexpected: next.unexpected || previous.unexpected || [],
    avcsChecked: details.length,
    avcsPresent: present.length,
    avcsMissing: details.length - present.length,
    avcsMismatched: present.filter(d => d.match === false).length,
  });
}

// The row menu of the grid: [{ key, label, disabled, reason, copyId? }]. Each
// item shows the server's reason when it is off, or why the row can not take it.
// Once Optus has re-pointed before the shut, a live copy is not force removed
// (the server's rule after the shut).
export function rowActionItems (row, steps, record, { earlyRepoint = false } = {}) {
  const NO_COPY = 'No target copy for this AVC';
  const EARLY_REMOVE = 'Optus appears to have re-pointed: rebuild instead of removing a live copy';
  const copy = ((record && record.targetCopies) || []).find(c => c.avcId === row.avcId && !c.removedAt);
  const hasCopy = row.targetBuildStatus === 'complete';
  const scopeReason = !row.inRouterScope ? 'Not in router scope' : !isVisp(row) ? 'L2 AVCs are not migrated yet' : '';
  const queuedReason = row.pendingEvent ? 'A change is queued for this AVC' : '';
  const item = (key, label, flag, ...reasons) => {
    if (!steps) return { key, label, disabled: true, reason: NO_PREFLIGHT_REASON };
    const own = reasons.find(Boolean);
    if (own) return { key, label, disabled: true, reason: own };
    return Object.assign({ key, label }, flagButton(steps, flag));
  };
  const remove = item('remove', 'Remove target copy (force)', copy && copy.orphan ? 'canRemoveTargetCopies' : 'canForceRemoveTargetCopy',
    copy ? '' : NO_COPY, earlyRepoint && copy && !copy.orphan ? EARLY_REMOVE : '');
  if (copy) remove.copyId = copy.id;
  const kickReason = !isVisp(row) ? 'Not on our RADIUS (L2)' : !row.radiusServiceId ? 'No RADIUS service for this AVC' : '';
  return [
    item('verify', 'Verify this AVC', 'canVerify', scopeReason),
    item('rebuild', 'Rebuild on target', hasCopy ? 'canRebuild' : 'canBuild', scopeReason, queuedReason),
    item('repolicy', 'Repolicy on target', 'canRepolicy', scopeReason, queuedReason, hasCopy ? '' : NO_COPY),
    remove,
    item('kick', 'Kick this service', 'canKick', kickReason),
  ];
}

// The Kick this / Verify this AVC buttons of a reconnection line take the same
// reasons as the grid row menu.
export function reconnectActions (avcId, rows, steps, record) {
  const row = (rows || []).find(r => r.avcId === avcId);
  if (!row) {
    const none = { disabled: true, reason: steps ? 'AVC is not in this CVC list' : NO_PREFLIGHT_REASON };
    return { kick: none, verify: none };
  }
  const items = rowActionItems(row, steps, record);
  const button = (key) => {
    const { disabled, reason } = items.find(i => i.key === key);
    return { disabled: !!disabled, reason: reason || '' };
  };
  return { kick: button('kick'), verify: button('verify') };
}

// ---- Section C: cutover ----

export const SHUT_TYPE = 'shutdown-cvc';
export const ROLLBACK_TYPE = 'no-shutdown-cvc';
const OVERRIDE_HINT = "If Optus has already re-pointed, use Confirm re-point with 'source shut failed'.";

export const statusOf = (event) => String((event && event.eventStatus) || '').toLowerCase();
const timeMs = (value) => {
  const date = toDate(value);
  return date ? date.getTime() : 0;
};
export const isNewer = (a, b) => timeMs(a.createdAt) > timeMs(b.createdAt)
  || (timeMs(a.createdAt) === timeMs(b.createdAt) && a.id > b.id);
const unshutCopies = (record) => ((record && record.sourceCopies) || []).filter(c => !c.removedAt && !c.shutAt);
export const plural = (n, one, many) => (n === 1 ? one : many);

export function newestEvent (record, types) {
  return ((record && record.events) || [])
    .filter(e => types.includes(e.eventType))
    .reduce((best, e) => (!best || isNewer(e, best) ? e : best), null);
}

// One status line for a shut or rollback event. Queued events say how long
// they have waited; the stalled cron hint comes with the queue time.
function eventLine (event, nowUtc, poiState, { rollback }) {
  const status = statusOf(event);
  const at = event.updatedAt || event.createdAt;
  const when = formatDual(at, poiState);
  if (status === 'pending') {
    const queued = queuedEventStatus({ status, createdAt: event.createdAt }, 'cvc', nowUtc);
    return { kind: 'pending', tone: 'amber', text: `${rollback ? 'Rollback ' : ''}${queued.text} (CVC cron)`, hint: queued.hint };
  }
  if (status === 'complete') {
    return rollback
      ? { kind: 'complete', tone: 'green', text: `Rollback complete ${when}. The source is back up.`, hint: '' }
      : { kind: 'complete', tone: 'green', text: `complete ${when} (queued ${clockOf(event.createdAt, SYDNEY)} SYD)`, hint: '' };
  }
  if (status === 'failed') {
    const reason = event.error || 'no reason recorded';
    return rollback
      ? { kind: 'failed', tone: 'red', text: `Rollback failed ${when}: ${reason}`, hint: '' }
      : { kind: 'failed', tone: 'red', text: `failed ${when}: ${reason}`, hint: OVERRIDE_HINT };
  }
  return { kind: 'unknown', tone: 'grey', text: status || 'unknown', hint: '' };
}

// The two status lines of Section C: the newest source shut, and a rollback
// that came after it (an older rollback is history once the source was shut again).
export function cutoverStatus (record, nowUtc, poiState) {
  const shut = newestEvent(record, [SHUT_TYPE]);
  const back = newestEvent(record, [ROLLBACK_TYPE]);
  return {
    shut: shut ? eventLine(shut, nowUtc, poiState, { rollback: false }) : null,
    rollback: back && (!shut || isNewer(back, shut)) ? eventLine(back, nowUtc, poiState, { rollback: true }) : null,
  };
}

// The source was shut and then brought back up, and nothing shut it again.
export function rolledBack (record) {
  const last = newestEvent(record, [SHUT_TYPE, ROLLBACK_TYPE]);
  return !!last && last.eventType === ROLLBACK_TYPE && statusOf(last) === 'complete';
}

// The Confirm re-point button and whether it needs the "shut failed" override.
export function confirmButton (steps) {
  if (!steps) return { disabled: true, reason: NO_PREFLIGHT_REASON, override: false };
  const override = steps.canConfirmWithFailedShut === true;
  if (override || steps.canConfirmRepoint === true) return { disabled: false, reason: '', override };
  const reason = steps.reasons && steps.reasons.canConfirmRepoint;
  return { disabled: true, reason: reason || NO_REASON_GIVEN, override: false };
}

export function shutLabel (preflight) {
  const record = preflight && preflight.record;
  if (record && record.repointConfirmedAt) return `Shut again (${unshutCopies(record).length} not shut)`;
  const shut = newestEvent(record, [SHUT_TYPE]);
  const back = newestEvent(record, [ROLLBACK_TYPE]);
  return shut && statusOf(shut) === 'failed' && (!back || isNewer(shut, back)) ? 'Retry shut' : 'Shut source';
}

export function rollbackLabel (preflight) {
  const record = preflight && preflight.record;
  const shut = newestEvent(record, [SHUT_TYPE]);
  const back = newestEvent(record, [ROLLBACK_TYPE]);
  return back && statusOf(back) === 'failed' && (!shut || isNewer(back, shut)) ? 'Retry rollback' : 'Rollback source';
}

// With `restoreCount` statics to put back first, the middle sentence says so.
export function rollbackConfirmText (preflight, restoreCount = 0) {
  const n = unremoved((preflight.record || {}).sourceCopies).filter(c => c.shutAt).length;
  const source = preflight.sourceHostname;
  const restore = restoreCount > 0
    ? `First the old IPs of ${restoreCount} committed ${plural(restoreCount, 'static', 'statics')} go back into RADIUS, then the rollback is queued. `
    : '';
  return `Bring the ${n} ${plural(n, 'subinterface', 'subinterfaces')} of ${preflight.cvcName} on ${source} back up? `
    + `Use this only if Optus has NOT re-pointed. ${restore}Service returns on ${source} after the CVC cron runs.`;
}

// Step 1 dialog. The booked window is checked here, but never blocks.
export function shutDialog (preflight, nowUtc) {
  const record = preflight.record || {};
  const cvc = preflight.cvcName;
  const n = record.repointConfirmedAt
    ? unshutCopies(record).length
    : (preflight.rows || []).filter(r => r.inRouterScope).length;
  const start = toDate(record.cutoverWindowStart);
  const end = toDate(record.cutoverWindowEnd);
  const now = toDate(nowUtc);
  const source = preflight.sourceHostname;
  let text = `This shuts ${n} ${plural(n, 'subinterface', 'subinterfaces')} for ${cvc} on ${source} in one change. `
    + `Every customer on this CVC loses service until Optus re-points the CVC to ${preflight.targetHostname}, or until you roll back.`;
  const alerts = [];
  if (start && end) {
    text += ` Booked window: ${windowShortText(start, end, preflight.poiState)}.`;
    if (now && (now < start || now > end)) {
      alerts.push({ type: 'warning', text: 'You are outside the booked window. Optus re-points only inside it.' });
    }
  } else {
    alerts.push({ type: 'warning', text: 'No window set' });
  }
  return {
    title: `Shut ${cvc} on ${source}`,
    body: [text],
    alerts,
    proceedLabel: `Shut ${source}`,
    requireTyping: true,
  };
}

// The banner for the operator to pass on to Optus once the source is shut. While
// a static with a reserved IP is not committed, `warning` is the amber line shown
// above it (it is not part of the text that is copied for Optus).
export function optusNotice (preflight) {
  if (!preflight || !preflight.steps || preflight.steps.phase !== 'waitingRepoint') return null;
  const shut = newestEvent(preflight.record, [SHUT_TYPE]);
  if (!shut || statusOf(shut) !== 'complete') return null;
  const at = shut.updatedAt || shut.createdAt;
  const poiZone = POI_TIMEZONE[preflight.poiState];
  const poi = poiZone && poiZone !== SYDNEY ? ` (${clockOf(at, poiZone)} ${ZONE_LABEL[poiZone]})` : '';
  const uncommitted = toCommit(preflight.rows).length;
  return {
    title: 'Pass this on: Optus can re-point now.',
    message: `${preflight.cvcName} (${preflight.cvcIdentifier}): ${preflight.sourceHostname} shut complete ${clockOf(at, SYDNEY)} SYD${poi}. Optus can re-point now.`,
    warning: uncommitted > 0
      ? `Commit ${staticsText(uncommitted)} before you pass this on. `
        + 'A static that reconnects before its commit keeps its old IP until Kick all after activation.'
      : '',
  };
}

// Step 2: minutes since the shut completed and what is left of the window.
export function waitingInfo (preflight, nowUtc) {
  const record = preflight && preflight.record;
  const shut = newestEvent(record, [SHUT_TYPE]);
  const shutAt = shut && statusOf(shut) === 'complete' ? toDate(shut.updatedAt || shut.createdAt) : null;
  const now = toDate(nowUtc);
  if (!shutAt || !now) return { text: '', tone: 'grey', advice: '' };
  let text = `Waiting ${Math.max(0, Math.floor((now - shutAt) / MINUTE_MS))} min since shut`;
  const end = toDate(record.cutoverWindowEnd);
  if (!end) return { text, tone: 'grey', advice: '' };
  const left = Math.ceil((end - now) / MINUTE_MS);
  if (left <= 0) return { text, tone: 'amber', advice: 'Window ended without a re-point' };
  text += ` . window ends in ${left} min`;
  if (left < 15) return { text, tone: 'amber', advice: 'Window ends soon: decide rollback or ask for the window to be extended' };
  return { text, tone: 'grey', advice: '' };
}

const EVIDENCE_PHASES = ['waitingRepoint', 'shutFailed', 'rollbackFailed'];
// Optus may re-point before the source is shut (20/10: sessions reached the target before the shut was queued).
const EARLY_PHASES = ['ready', 'shutPending'];
const NOT_AVAILABLE_YET = 'Not available yet';

// The reconnection answer names the target NAS and one row per AVC only from
// the second version of VispApi's check. Until then its fields are missing, so
// the page says so instead of printing "undefined".
const hasEvidence = (reconnect) => !!(reconnect && reconnect.targetNasIp);

// Services the reconnection check watches: not L2 or network only, not suspended.
export const monitoredCount = (reconnect) => ((reconnect && reconnect.rows) || [])
  .filter(r => !NOT_ON_OUR_RADIUS.includes(r.reason) && !isSuspended(r)).length;

// Monitored services whose latest session since the reference is on the target
// NAS. VispApi's onTargetCount also counts suspended services, which
// monitoredCount leaves out, so "k of m" could pass m.
export const monitoredOnTarget = (reconnect) => {
  const nas = reconnect && reconnect.targetNasIp;
  const suspendedThere = ((reconnect && reconnect.rows) || [])
    .filter(r => isSuspended(r) && String(r.lastNasIp || '').trim() === nas).length;
  return Math.max(0, ((reconnect && reconnect.onTargetCount) || 0) - suspendedThere);
};

// Optus moves the CVC around its booked window, so before the shut the page
// watches from an hour before the start to a day after the end (a Ready tab can
// stay open for days, and a dropped booking can leave the record in Ready).
export const EARLY_WATCH_MS = 60 * MINUTE_MS;
export const EARLY_WATCH_AFTER_END_MS = 24 * 60 * MINUTE_MS;

function watchingEarly (preflight, now) {
  const phase = preflight.steps.phase;
  if (phase === 'shutPending') return true;
  if (phase !== 'ready') return false;
  const record = preflight.record;
  const start = toDate(record && record.cutoverWindowStart);
  const end = toDate(record && record.cutoverWindowEnd) || start;
  const at = toDate(now);
  if (!start || !at) return false;
  const t = at.getTime();
  return t >= start.getTime() - EARLY_WATCH_MS && t < end.getTime() + EARLY_WATCH_AFTER_END_MS;
}

// The re-point evidence matters from the moment the CVC is ready to the confirm
// (Optus may re-point before the shut), and after a rollback. Before the shut it
// shows inside the watch, or once an answer has sessions on the target.
export function showEvidence (preflight, now, reconnect) {
  const steps = preflight && preflight.steps;
  if (!steps) return false;
  if (EVIDENCE_PHASES.includes(steps.phase)) return true;
  const record = preflight.record;
  if (record && record.repointConfirmedAt) return false;
  if (watchingEarly(preflight, now) || repointedEarly(preflight, reconnect)) return true;
  return PRE_CUT_PHASES.includes(steps.phase) && rolledBack(record);
}

// Sessions are already on the target before the source shut, and nothing was
// rolled back or confirmed. The red line and panel R both read this.
export function repointedEarly (preflight, reconnect) {
  const phase = preflight && preflight.steps && preflight.steps.phase;
  const record = preflight && preflight.record;
  return EARLY_PHASES.includes(phase) && !rolledBack(record) && !(record && record.repointConfirmedAt)
    && hasEvidence(reconnect) && monitoredOnTarget(reconnect) > 0;
}

// Step 2 evidence from the reconnection check: sessions that reached the target
// NAS since the reference time (the start of the migration record). L2 and
// network-only services are not counted.
export function repointEvidence (preflight, reconnect) {
  if (!reconnect) return { line: '', tone: 'grey', message: '', onTargetCount: null };
  if (!hasEvidence(reconnect)) {
    return { line: `Sessions on the target NAS since this migration started: ${NOT_AVAILABLE_YET}`, tone: 'grey', message: '', onTargetCount: null };
  }
  const monitored = monitoredCount(reconnect);
  const count = monitoredOnTarget(reconnect);
  const nas = reconnect.targetNasIp;
  const source = preflight.sourceHostname;
  const line = `Sessions on ${nas} since ${formatDual(reconnect.referenceUtc, preflight.poiState)}: ${count} of ${monitored} services`;
  const record = preflight.record;
  const phase = preflight.steps && preflight.steps.phase;
  let message = '';
  let tone = 'grey';
  if (count > 0 && rolledBack(record) && !(record && record.repointConfirmedAt)) {
    message = `Sessions are arriving on ${nas} although ${source} was rolled back: Optus re-pointed. `
      + `Shut ${source} again, commit statics again, then confirm.`;
    tone = 'red';
  } else if (count > 0 && EVIDENCE_PHASES.includes(phase)) {
    message = `Optus appears to have re-pointed (sessions on ${nas})`;
    tone = 'green';
  } else if (repointedEarly(preflight, reconnect)) {
    const next = phase === 'shutPending' ? `Let the ${source} shut finish` : `Shut ${source}`;
    message = `Sessions are already arriving on ${nas} (${count} of ${monitored}): Optus appears to have re-pointed before the ${source} shut. `
      + `${next}, commit statics, then confirm the re-point.`;
    tone = 'red';
  }
  return { line, tone, message, onTargetCount: count };
}

const failedShutOverride = (preflight) => !!(preflight && preflight.steps && preflight.steps.canConfirmWithFailedShut === true);

// Step 3 dialog: the evidence first, then who confirmed. No session on the
// target is a warning, not a block (a network engineer can still vouch).
export function confirmDialog (preflight, reconnect) {
  const cvc = preflight.cvcName;
  const target = preflight.targetHostname;
  const shut = newestEvent(preflight.record, [SHUT_TYPE]);
  const body = [];
  const alerts = [];
  if (shut && statusOf(shut) === 'complete') {
    body.push(`${preflight.sourceHostname} shut complete at ${formatDual(shut.updatedAt || shut.createdAt, preflight.poiState)}.`);
  } else if (shut && statusOf(shut) === 'failed') {
    alerts.push({
      type: 'error',
      text: `${preflight.sourceHostname} shut FAILED at ${formatDual(shut.updatedAt || shut.createdAt, preflight.poiState)}: ${shut.error || 'no reason recorded'}`,
    });
  }
  const evidence = repointEvidence(preflight, reconnect);
  if (evidence.line) body.push(`${evidence.line}.`);
  body.push(`After this, going back to ${preflight.sourceHostname} needs Optus to re-point back (a booked change). Fix forward on ${target} where you can.`);
  const check = `Confirm only if a network engineer has checked ${cvc} on ${target}.`;
  if (reconnect && evidence.onTargetCount === 0) {
    alerts.push({ type: 'warning', text: `No session has reached ${reconnect.targetNasIp} since this migration started. ${check}` });
  } else if (!reconnect) {
    alerts.push({ type: 'warning', text: `Session evidence could not be read. ${check}` });
  } else if (!hasEvidence(reconnect)) {
    alerts.push({ type: 'warning', text: `Session evidence is not available yet. ${check}` });
  }
  // The one required tick: the failed shut override says Optus has already
  // re-pointed, otherwise the tick says Optus confirmed it. The name is not typed.
  const checkbox = failedShutOverride(preflight)
    ? { label: 'The source shut failed and Optus has already re-pointed (recorded as an override)', shown: true, required: true }
    : { label: 'Optus has confirmed the re-point', shown: true, required: true };
  return {
    title: `Confirm Optus re-pointed ${cvc}`,
    body,
    alerts,
    extra: { label: 'Confirmed by', hint: `e.g. Name - CVC up on ${target}`, maxlength: 50, required: true },
    checkbox,
    proceedLabel: 'Confirm re-point',
    requireTyping: false,
  };
}

// The confirm re-point call from what the dialog answered. Only the override
// tick records a failed shut; the Optus tick of a completed shut never does.
export function confirmRepointRequest (preflight, { extra, checked }) {
  return { confirmedBy: extra, sourceShutFailed: failedShutOverride(preflight) && !!checked };
}

// Step 4 dialog. The LOCAL customer name is shown when VispApi sends it.
export function activateDialog (preflight) {
  const cvc = preflight.cvcName;
  const target = preflight.targetHostname;
  const vispCount = (preflight.rows || []).filter(r => isVisp(r) && r.serviceStatus !== 'inactive-delete').length;
  const network = preflight.localNetwork || {};
  const local = network.customerName || network.bridgeDomain || 'the LOCAL network';
  return {
    title: `Activate ${cvc} on ${target}`,
    body: [
      `Move ${cvc} to ${target} in Usher: CVC pointer -> ${target}, ${vispCount} VISP AVCs -> ${local}, `
      + 'new orders on this CVC go LOCAL. No router change. Undone only through section R, after Optus re-points back.',
      'After this: Kick all (section D) for services that are not back on their new IP.',
    ],
    proceedLabel: 'Activate',
    requireTyping: false,
  };
}

// Kick all opens only after Activate (Commit opens with the shut): under step 4
// the page says what comes next. The server owns the rule (steps.reasons), this
// is the hint.
export function activationNextLine (preflight) {
  const steps = preflight && preflight.steps;
  if (!steps || steps.phase !== 'repointConfirmed') return '';
  const uncommitted = toCommit(preflight.rows).length;
  const target = preflight.targetHostname;
  return uncommitted > 0
    ? `Next: Commit ${staticsText(uncommitted)}, then Activate on ${target}. Kick all opens after activation.`
    : `Next: Activate on ${target}. Kick all opens after activation.`;
}

// A record confirmed and not activated that already had Kick all run (the order
// was not enforced before): the stamp is shown so the operator activates now and
// checks Reconnection for services still on an old IP. A commit before
// activation is normal now and gets no banner.
export function outOfOrderBanner (preflight) {
  const record = preflight && preflight.record;
  if (!record || !record.repointConfirmedAt || record.activatedAt || !record.kickedAt) return null;
  return {
    type: 'warning',
    text: `Kick all ran before activation (${formatDual(record.kickedAt, preflight.poiState)}). `
      + `Activate on ${preflight.targetHostname} now, then use Reconnection to find services still on an old IP.`,
  };
}

export function activationSummary (result) {
  const count = (key) => ((result && result[key]) || []).length;
  return `Swapped ${count('swapped')} . already local ${count('alreadyLocal')} . kept customer (L2) ${count('keptCustomer')}`;
}

// Usher refuses an activation with extras VispApi passes through in `data`:
// AVC ids with a queued change, or LOCAL VLAN conflicts. Both name keys are
// read in camelCase and snake_case (the pass-through is not re-cased).
export function activationRefusal (e) {
  if (!e || !e.response || e.response.status !== 409) return null;
  const body = e.response.data;
  const extra = body && typeof body === 'object' && body.data && typeof body.data === 'object' ? body.data : null;
  if (!extra) return null;
  const pending = extra.pendingAvcIds || extra.pending_avc_ids;
  if (Array.isArray(pending) && pending.length) {
    const verb = pending.length === 1 ? 'has' : 'have';
    return {
      kind: 'pending',
      type: 'warning',
      text: `${pending.join(', ')} ${verb} a queued Usher change. Wait for the AVC cron, then activate again.`,
      conflicts: [],
    };
  }
  if (Array.isArray(extra.conflicts) && extra.conflicts.length) {
    return {
      kind: 'conflicts',
      type: 'error',
      text: 'Activation refused. Data fix needed (dev). Commit and Kick wait for activation.',
      conflicts: extra.conflicts.map(c => ({
        avcId: c.avcId || c.avc_id,
        innerVlan: c.innerVlan !== undefined ? c.innerVlan : c.inner_vlan,
        heldByAvcId: c.heldByAvcId || c.held_by_avc_id,
      })),
    };
  }
  return null;
}

// What is left to do after Activate: AVCs not built on the target (customers
// without service), copies not shut on the source, and verify mismatches. The lists
// start from the activation answer (or from live data after a reload) and
// shrink as live data shows them fixed.
export function followUps (result, lastVerify, live) {
  let notBuilt = [];
  let notShut = [];
  if (result) {
    notBuilt = (result.notBuilt || []).slice();
    notShut = (result.notShut || []).slice();
  } else if (live && live.activated) {
    notBuilt = (live.rows || []).filter(r => inScope(r) && r.targetBuildStatus !== 'complete').map(r => r.avcId);
    notShut = unshutCopies(live.record).map(c => c.avcId);
  }
  if (live && result) {
    const byId = indexBy(live.rows, 'avcId');
    const unshut = unshutCopies(live.record).map(c => c.avcId);
    notBuilt = notBuilt.filter(id => byId[id] && byId[id].targetBuildStatus !== 'complete');
    notShut = notShut.filter(id => unshut.includes(id));
  }
  const details = (lastVerify && lastVerify.details) || [];
  const repolicy = details.filter(d => driftKind(d) === 'policy').map(d => d.avcId);
  const rebuild = details.filter(d => driftKind(d) === 'rebuild').map(d => d.avcId);
  const items = [];
  const add = (key, tone, label, ids, actionLabel, flag) => {
    if (ids.length) items.push({ key, tone, text: `${label} ${ids.length}`, ids, actionLabel, flag });
  };
  add('notBuilt', 'red', 'Not built on target', notBuilt, 'Build now', 'canBuild');
  add('notShut', 'amber', 'Not shut on source', notShut, 'Shut again', 'canShutdown');
  add('repolicy', 'amber', 'Speed policy differs on target', repolicy, 'Repolicy', 'canRepolicy');
  add('rebuild', 'amber', 'Wrong or missing on target', rebuild, 'Rebuild', 'canRebuild');
  return { items, empty: items.length === 0 };
}

// ---- Section D: kick and reconnection ----

export const RECONNECT_REFRESH_MS = 60000;
const AFTER_CONFIRM_PHASES = ['repointConfirmed', 'activated'];

// The reconnection check is read-only, so the page polls it (silently) while
// the operator needs it: before the confirm to see Optus's move, after a
// rollback to notice a re-point anyway, and after the confirm unless switched off.
export function shouldPollReconnect ({ preflight, reconnect, autoOn, nowMs, lastMs, visible, busy }) {
  const steps = preflight && preflight.steps;
  if (!steps || steps.canVerifyReconnected !== true || !visible || busy) return false;
  if (nowMs - lastMs < RECONNECT_REFRESH_MS) return false;
  // The rollback panel reads the sessions (back on the source, dropped from the target) until it is closed.
  if (steps.phase === 'rollingBack') return true;
  if (showEvidence(preflight, nowMs, reconnect)) return true;
  return AFTER_CONFIRM_PHASES.includes(steps.phase) && !!autoOn;
}

const REBOOT_FROM_MIN = 5;
const FIX_FORWARD_AFTER_MIN = 15;

// VISP services with a RADIUS service: the ones a kick can reach. A static whose
// new IP is reserved but not committed would come back with its old IP.
const kickable = (rows) => (rows || []).filter(r => isVisp(r) && r.radiusServiceId);
const uncommittedStatic = (r) => r.ipType === 'Static' && !!r.hasPendingIp && !r.committed;
const radiusId = (r) => r.radiusServiceId;

// The rows of the reconnection answer, or null while there is none (an older
// answer has no target NAS and no rows): "not checked yet".
export function reconnectRowsOf (reconnect) {
  return hasEvidence(reconnect) && Array.isArray(reconnect.rows) ? reconnect.rows : null;
}

// Kickable services whose latest reconnection row (matched by RADIUS service id)
// says they are back on the target with their expected IP.
const onlineServiceIds = (reconnectRows) => (Array.isArray(reconnectRows) ? reconnectRows : [])
  .filter(r => r.reason === 'reconnected' && r.radiusServiceId !== null && r.radiusServiceId !== undefined)
  .map(radiusId);

// Services to drop by default: not an uncommitted static (it would come back
// with its old IP) and not already back online.
const sentRows = (rows, reconnectRows) => {
  const online = onlineServiceIds(reconnectRows);
  return kickable(rows).filter(r => !uncommittedStatic(r) && !online.includes(radiusId(r)));
};

export function defaultKickSubset (rows, reconnectRows) {
  return sentRows(rows, reconnectRows).map(radiusId);
}

export function kickPlan (rows, reconnectRows) {
  const all = kickable(rows);
  const held = all.filter(uncommittedStatic);
  const onlineIds = onlineServiceIds(reconnectRows);
  const online = all.filter(r => !uncommittedStatic(r) && onlineIds.includes(radiusId(r)));
  const sent = sentRows(rows, reconnectRows);
  return {
    ids: defaultKickSubset(rows, reconnectRows),
    heldIds: held.map(radiusId),
    allIds: all.map(radiusId),
    onlineIds: online.map(radiusId),
    statics: sent.filter(r => r.ipType === 'Static').length,
    dynamics: sent.filter(r => r.ipType === 'Dynamic').length,
    held: held.length,
    online: online.length,
    answered: Array.isArray(reconnectRows),
  };
}

// "6 services (4 static, 2 dynamic)"
export function kickLine (plan) {
  const held = plan.held > 0 ? ` . ${plan.held} uncommitted statics held back` : '';
  return `${plan.ids.length} services (${plan.statics} static, ${plan.dynamics} dynamic)${held}`;
}

// The Kick all dialog. By default it drops the services that are not back on the
// target with their expected IP; two ticks add the uncommitted statics and the
// services already online. Without a reconnection answer every service is in.
export function kickDialog (rows, target, includeUncommitted, { lastRun, poiState, reconnectRows, includeOnline } = {}) {
  const plan = kickPlan(rows, reconnectRows);
  const selected = kickable(rows).filter(r => {
    if (uncommittedStatic(r)) return !!includeUncommitted;
    return plan.onlineIds.includes(radiusId(r)) ? !!includeOnline : true;
  });
  const ids = selected.map(radiusId);
  const statics = selected.filter(r => r.ipType === 'Static').length;
  const dynamics = selected.filter(r => r.ipType === 'Dynamic').length;
  let text;
  if (ids.length === 0 && plan.online > 0) {
    text = `All ${plan.online} services are back on ${target}. Nothing needs a kick.`;
  } else if (plan.answered && !includeOnline) {
    text = `Drop the current session of ${ids.length} services that are not back on ${target} with their expected IP: `
      + `${statics} statics, ${dynamics} dynamics.`;
    if (plan.online > 0) text += ` Not sent: ${plan.online} services already online on ${target}.`;
  } else {
    text = `Drop the current session of ${ids.length} services so they reconnect through ${target}: ${statics} statics, ${dynamics} dynamics.`;
  }
  if (plan.held > 0 && !includeUncommitted) {
    text += ` Excluded: ${plan.held} statics not yet committed (they would come back with their old IP).`;
  }
  if (!plan.answered) text += ' Reconnection not checked yet: every service is included.';
  return {
    ids,
    text,
    proceedText: 'Kick',
    checkboxLabel: plan.held > 0 ? `Include the ${plan.held} uncommitted statics` : '',
    onlineCheckboxLabel: plan.online > 0 ? `Include the ${plan.online} services already online` : '',
    lastLine: lastKickLine(lastRun, poiState),
  };
}

export function kickOneText (row, target) {
  const warning = uncommittedStatic(row) ? ' Its new IP is not committed yet: it comes back with its old IP.' : '';
  return `Drop the current session of ${row.radiusUsername} so it reconnects through ${target}?${warning}`;
}

// ---- Kick run: the server owns it, the page follows it ----

export const KICK_ADOPT_IDLE_SECONDS = 15;
const KICK_POLL_MS = 3000;
const KICK_READ_TRIES = 3;
const KICK_UNKNOWN_TEXT = 'Result unknown: VispApi restarted. Check Reconnection, then Retry failed and not sent.';
const KICK_UNREADABLE_TEXT = 'Result unknown: could not read the Kick all state. Check Reconnection, then Retry failed and not sent.';
const KICK_MAX_TEXT = 'Kick all is still running on the server. Reload the page to follow it.';
const KICK_RESTART_WAIT_MIN = 2;
const KICK_STAMP_NOTE = 'Kick time not saved in Usher (it did not answer in time). Minutes since kick under Reconnection may be off.';

export function newKickRunId () {
  return `kick-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// An answer without a status is a finished run (an older VispApi).
const isRunning = (run) => !!run && run.status === 'running';
const hasKickAnswer = (run) => !!run && run.status !== 'none';

// One key per service for the rows kept across runs (a service skipped as
// ambiguous has no RADIUS service, only its AVC).
export const kickKey = (row) => (row.radiusServiceId !== null && row.radiusServiceId !== undefined
  ? `r${row.radiusServiceId}`
  : `a${row.avcId}`);

// 409 "already running" carries the running run in data.kickRun.
const conflictRun = (e) => {
  const body = e && e.response && e.response.status === 409 ? e.response.data : null;
  return (body && body.data && body.data.kickRun) || null;
};
const isConflict = (e) => !!(e && e.response && e.response.status === 409);
// The answer never came back (page timeout, dropped connection, proxy 502/504):
// the run may still be going, so the page reads it instead of reporting a failure.
const isLostAnswer = (e) => {
  if (!e) return false;
  if (!e.response) return e.code === 'ECONNABORTED' || !!e.request;
  return e.response.status === 502 || e.response.status === 504;
};
// A run of restored statics is never stamped (a rollback writes no forward stamp), so it never asks for the retry.
const needsStampRetry = (run) => !!run && run.kind !== 'restored' && run.status === 'finished' && run.stamped === false && (run.done || 0) > 0;

// The first call starts the run (ids, run id, kickAll), every later call
// continues it while it is `running`. Nothing is lost when an answer is: the
// run is read, and a run that another tab or a reload left idle for 15 s is
// continued from here. Resolves { kind, run, message, error }:
// done (the run of this call ended) | busy (another run was running: followed
// to its end, or refused at once with follow false) | unknown | error | max | stopped.
export async function runKick ({ ids, kickAll, restored, runId, adopt, follow = true, send, read, onProgress, sleep, maxCalls = 60, shouldStop }) {
  const pause = sleep || (ms => new Promise(resolve => setTimeout(resolve, ms)));
  const own = runId || newKickRunId();
  let target = own;
  let params = adopt
    ? { runId: own, continueRun: true }
    : { radiusServiceIds: ids, runId: own, kickAll: !!kickAll, ...(restored ? { restored: true } : {}) };
  let sends = 0;
  let reads = 0;
  let last = null;
  let stampTried = false;
  const following = () => target !== own;
  const show = (run) => {
    last = run;
    if (onProgress) onProgress({ run, busy: following() });
  };
  const end = (kind, extra = {}) => ({ kind, run: last, message: '', ...extra });
  const look = async () => {
    for (let i = 0; i < KICK_READ_TRIES; i++) {
      if (i > 0) await pause(KICK_POLL_MS);
      try {
        return await read();
      } catch (e) {
        // read again
      }
    }
    return undefined;
  };

  for (;;) {
    if (shouldStop && shouldStop()) return end('stopped');
    if (sends >= maxCalls) return end('max', { message: KICK_MAX_TEXT });
    sends++;
    let run = null;
    let failure = null;
    try {
      run = await send(params);
    } catch (e) {
      failure = e;
    }

    let seen;
    if (!failure) {
      show(run);
      if (isRunning(run)) {
        params = { runId: target, continueRun: true };
        continue;
      }
      if (needsStampRetry(run) && !stampTried) {
        stampTried = true;
        try {
          show(await send({ runId: target, continueRun: true }));
        } catch (e) {
          // the first answer stays
        }
      }
      return end(following() ? 'busy' : 'done');
    }
    const other = conflictRun(failure);
    if (other) {
      if (!follow) {
        target = other.runId;
        show(other);
        return end('busy');
      }
      seen = other;
    } else if (isConflict(failure) && !params.continueRun) {
      return end('error', { message: errorMessage(failure), error: failure });
    } else if (isConflict(failure) || isLostAnswer(failure)) {
      seen = await look();
      if (seen === undefined) return end('unknown', { message: KICK_UNREADABLE_TEXT });
    } else {
      return end('error', { message: errorMessage(failure), error: failure });
    }

    // Follow the run by reading it until it ends or has sat idle long enough to continue.
    for (;;) {
      if (!hasKickAnswer(seen)) return end('unknown', { message: KICK_UNKNOWN_TEXT });
      if (seen.runId !== target) {
        if (!isRunning(seen)) return end('unknown', { message: KICK_UNKNOWN_TEXT });
        target = seen.runId;
      }
      show(seen);
      if (!isRunning(seen)) return end(following() ? 'busy' : 'done');
      if ((seen.idleSeconds || 0) >= KICK_ADOPT_IDLE_SECONDS) {
        params = { runId: target, continueRun: true };
        break;
      }
      if (reads >= maxCalls * 5) return end('max', { message: KICK_MAX_TEXT });
      reads++;
      await pause(KICK_POLL_MS);
      seen = await look();
      if (seen === undefined) return end('unknown', { message: KICK_UNREADABLE_TEXT });
    }
  }
}

// Read a run this tab does not drive; continue one that went idle.
export function shouldReadKickRun ({ run, kickLoop }) {
  return !kickLoop && isRunning(run);
}

export function shouldAdoptKickRun (run, kickLoop) {
  return !kickLoop && isRunning(run) && (run.idleSeconds || 0) >= KICK_ADOPT_IDLE_SECONDS;
}

const kickWhen = (run, poiState) => formatDual(run.finishedAt || run.startedAt, poiState);

// "Kick all 20/10 11:09 SYD: 4 requested, 2 failed, 0 skipped, 1 not sent". Red
// when any failed, amber when something was skipped or not sent, else green.
// noKick is the red line for a run in which no kick was accepted.
// A run of the statics put back on their old IP is named for what it is.
const kickName = (run) => (run && run.kind === 'restored' ? 'Kick restored statics' : 'Kick all');

export function kickRunSummary (run, poiState) {
  const requested = run.done || 0;
  const failed = run.failed || 0;
  const skipped = run.skipped || 0;
  const notSent = run.notSent || 0;
  const when = kickWhen(run, poiState);
  return {
    type: failed > 0 ? 'error' : skipped + notSent > 0 ? 'warning' : 'success',
    text: `${kickName(run)} ${when}: ${requested} requested, ${failed} failed, ${skipped} skipped${notSent > 0 ? `, ${notSent} not sent` : ''}`,
    noKick: requested === 0 && (run.toKick || 0) > 0 ? `Last ${kickName(run)} ${when}: no kick was accepted` : '',
  };
}

export function kickProgressText (run) {
  return `Kicking ${run.toKick || 0} services: ${run.answered || 0} of ${run.toKick || 0} answered`;
}

export function kickRunningText (run, poiState) {
  return `${kickName(run)} running since ${formatDual(run.startedAt, poiState)} by ${run.startedBy}`;
}

export function kickBusyText (run, poiState) {
  return `${kickName(run)} is already running (started ${formatDual(run.startedAt, poiState)} by ${run.startedBy}). Wait for it to finish.`;
}

// The stamp ("kicked {t}") is saved by the last call of a run; when it could not
// be saved the minutes since kick under Reconnection may be off.
export function kickStampNote (run) {
  return needsStampRetry(run) ? KICK_STAMP_NOTE : '';
}

export function lastKickLine (run, poiState) {
  if (!run) return '';
  return `Last Kick all ${kickWhen(run, poiState)} by ${run.startedBy}: ${run.done || 0} requested, ${run.failed || 0} failed. `
    + 'Running it again drops every session once more.';
}

// The latest run that ended and was a Kick all (a Kick this is not one).
export function lastKickAllRun (runs) {
  return (runs || [])
    .filter(r => r && r.kind === 'all' && (r.status === 'finished' || r.status === 'stopped'))
    .reduce((best, r) => (!best || timeMs(r.startedAt) > timeMs(best.startedAt) ? r : best), null);
}

const KICK_OUTCOME_ORDER = { failed: 0, notSent: 1, skipped: 2, done: 3, pending: 4 };
const KICK_OUTCOME_TONE = { failed: 'red', notSent: 'amber', skipped: 'amber', done: 'green', pending: 'grey' };
const kickRank = (outcome) => (KICK_OUTCOME_ORDER[outcome] === undefined ? 5 : KICK_OUTCOME_ORDER[outcome]);

// The per-service lines of a run: failures first, then not sent, skipped, requested.
export function kickRows (run) {
  return ((run && run.results) || [])
    .map(r => ({
      avcId: r.avcId,
      radiusServiceId: r.radiusServiceId,
      outcome: r.outcome,
      message: r.message,
      reason: r.reason,
      sentAt: r.sentAt,
      tone: KICK_OUTCOME_TONE[r.outcome] || 'grey',
    }))
    .sort((a, b) => kickRank(a.outcome) - kickRank(b.outcome));
}

// A kick that was sent beats a row that was not; between sent kicks the later wins.
const kickRowWins = (next, old) => {
  const n = timeMs(next.sentAt);
  const o = timeMs(old.sentAt);
  if (n && o) return n >= o;
  if (n) return true;
  return !o;
};

// The latest kick per service across the runs this tab has seen.
export function mergeKickRows (previous, run) {
  const merged = Object.assign({}, previous);
  ((run && run.results) || []).filter(r => r.outcome !== 'pending').forEach(r => {
    const key = kickKey(r);
    if (!merged[key] || kickRowWins(r, merged[key])) merged[key] = Object.assign({}, r);
  });
  return merged;
}

// The session data, not the kick answer, proves a kick worked (BR-41). A session
// that started after the kick was sent is a restart, whatever the kick answered.
// A kick that was accepted and left the session as it was, 2 minutes on, did not work.
export function kickProof (kickRow, reconnectRow, nowUtc) {
  const sent = toDate(kickRow && kickRow.sentAt);
  if (!sent || !reconnectRow) return null;
  const started = toDate(reconnectRow.lastSessionStart);
  if (started && started >= sent) {
    return { kind: 'restarted', tone: 'green', text: `Session restarted ${sydneyTime(started)}` };
  }
  const now = toDate(nowUtc);
  if (kickRow.outcome === 'done' && now && now - sent >= KICK_RESTART_WAIT_MIN * MINUTE_MS) {
    return { kind: 'notRestarted', tone: 'amber', text: `Kick requested at ${sydneyTime(sent)} but the session did not restart` };
  }
  return null;
}

// Failed and not sent services are retried; a kick that was accepted but did not
// restart the session is not (it needs a reset outside the tool).
export function retryKickIds (items) {
  return (items || [])
    .filter(i => (i.outcome === 'failed' || i.outcome === 'notSent') && i.radiusServiceId !== null && i.radiusServiceId !== undefined)
    .map(i => i.radiusServiceId);
}

const GUIDANCE_NOT_SENT = ['stopped', 'pageClosed'];

// "Sessions that did not restart (n)": failed kicks, services the run stopped
// before, and accepted kicks whose session did not restart. A service whose
// session restarted after the kick is not listed, whatever the kick answered.
export function kickGuidance ({ kickRows: kicks, reconnect, rows, wholesalerNames, nowUtc }) {
  const none = { show: false, title: '', body: '', count: 0, items: [], staticsOldIp: 0, retryIds: [] };
  const sessions = indexBy(hasEvidence(reconnect) ? reconnect.rows : [], 'avcId');
  const grid = indexBy(rows, 'avcId');
  const items = [];
  Object.keys(kicks || {}).map(key => kicks[key]).forEach(k => {
    const session = sessions[k.avcId];
    const proof = kickProof(k, session, nowUtc);
    if (proof && proof.kind === 'restarted') return;
    const listed = k.outcome === 'failed'
      || (k.outcome === 'notSent' && GUIDANCE_NOT_SENT.includes(k.reason))
      || (k.outcome === 'done' && !!proof && proof.kind === 'notRestarted');
    if (!listed) return;
    items.push({
      avcId: k.avcId,
      radiusServiceId: k.radiusServiceId,
      wholesaler: grid[k.avcId] ? wholesalerText(grid[k.avcId], wholesalerNames) : '',
      user: session ? session.radiusUsername : '',
      ipType: session ? session.ipType : '',
      sessionIp: session ? session.lastAcctIp : '',
      expectedIp: session ? session.expectedIp : '',
      kickResult: k.message,
      outcome: k.outcome,
      minutes: minutesSince(k.sentAt, nowUtc),
      staticOldIp: !!session && session.ipType === 'Static' && OLD_IP_REASONS.includes(session.reason),
    });
  });
  if (!items.length) return none;
  items.sort((a, b) => kickRank(a.outcome) - kickRank(b.outcome));
  const statics = items.filter(i => i.staticOldIp).length;
  // No reconnection answer yet (after a load, in phase migrated, or when the check
  // failed) or an answer without the target NAS: name the target without its IP.
  const nas = hasEvidence(reconnect) ? reconnect.targetNasIp : '';
  return {
    show: true,
    title: `Sessions that did not restart (${items.length})`,
    body: `These services are still on their old session${statics > 0 ? `, and ${statics} statics still have their old IP` : ''}. `
      + 'Restart each one outside the tool, then press Refresh under Reconnection. '
      + '1. Reset the service from the NBN side (port reset; line resync for FTTN). '
      + `2. If that does not help, ask the network team to clear the session on the target BNG${nas ? ` (${nas})` : ''}. `
      + `3. Or ask the wholesaler to power-cycle the modem. A row turns green when a new session starts on ${nas || 'the target BNG'}${statics > 0 ? ' with the new IP' : ''}.`,
    count: items.length,
    items,
    staticsOldIp: statics,
    retryIds: retryKickIds(items),
  };
}

export function toKickGuidanceCsv (items, { cvcName, nowUtc }) {
  const columns = [
    { header: 'Wholesaler', value: 'wholesaler' },
    { header: 'AVC', value: 'avcId' },
    { header: 'Username', value: 'user' },
    { header: 'IP type', value: 'ipType' },
    { header: 'Session IP now', value: 'sessionIp' },
    { header: 'Expected IP', value: 'expectedIp' },
    { header: 'Kick result', value: 'kickResult' },
    { header: 'Minutes since kick', value: 'minutes' },
  ];
  const name = String(cvcName).replace(/[\\/:*?"<>|]/g, '-');
  return { filename: `${name}_sessions_not_restarted_${csvStamp(nowUtc)}_SYD.csv`, text: toCsv(columns, items || []) };
}

// After a result nobody can read (VispApi restarted): retry what the last
// snapshot still had open, else everything this tab sent.
export function kickUnknownResult (lastRun, sentIds) {
  const open = ((lastRun && lastRun.results) || [])
    .filter(r => ['failed', 'notSent', 'pending'].includes(r.outcome) && r.radiusServiceId !== null && r.radiusServiceId !== undefined)
    .map(r => r.radiusServiceId);
  return { message: KICK_UNKNOWN_TEXT, retryIds: open.length ? open : (sentIds || []).slice() };
}

// The alert under Kick this: "{avc}: {outcome text}".
export function kickThisAlert (result, row, poiState) {
  if (result.kind === 'busy') {
    return { type: 'warning', text: result.run ? kickBusyText(result.run, poiState) : 'Kick all is already running. Wait for it to finish.' };
  }
  if (result.kind === 'error') return { type: 'error', text: `${row.avcId}: ${result.message}` };
  if (result.kind !== 'done') return { type: 'warning', text: `${row.avcId}: ${result.message}` };
  const found = ((result.run && result.run.results) || []).find(r => r.radiusServiceId === row.radiusServiceId);
  if (!found) return { type: 'warning', text: `${row.avcId}: no result came back` };
  return { type: found.outcome === 'done' ? 'info' : 'warning', text: `${row.avcId}: ${found.message}` };
}

function minutesSince (utc, nowUtc) {
  const from = toDate(utc);
  const to = toDate(nowUtc);
  return from && to ? Math.max(0, Math.floor((to - from) / MINUTE_MS)) : null;
}

// What to tell the operator about a service that is not back, by minutes since
// the kick.
export function reconnectAdvice (row, kickedAt, nowUtc) {
  const minutes = minutesSince(kickedAt, nowUtc);
  if (isSuspended(row)) return { text: SUSPENDED_ADVICE, tone: 'grey', minutes };
  if (row.reconnected) return { text: '', tone: 'green', minutes };
  if (minutes === null) return { text: 'Not kicked yet', tone: 'grey', minutes: null };
  if (minutes < REBOOT_FROM_MIN) return { text: 'Redialling', tone: 'grey', minutes };
  if (minutes <= FIX_FORWARD_AFTER_MIN) return { text: 'CPE may need a reboot (wholesaler)', tone: 'amber', minutes };
  return { text: 'Fix forward: Verify this AVC, Rebuild, Repolicy, re-Commit, Kick', tone: 'red', minutes };
}

// ---- Reconnection verdicts: one text per reason VispApi sends ----

const OLD_IP_REASONS = ['oldIpNotCommitted', 'oldIpBeforeCommit', 'oldIpAfterCommit'];
// VispApi gives these only for an open, fresh session on the target NAS.
const liveOnTarget = (r) => r.reconnected === true || OLD_IP_REASONS.includes(r.reason);
const sydneyTime = (utc) => {
  const text = formatInZone(utc, SYDNEY);
  return text ? `${text} SYD` : '';
};
const withText = (prefix, value) => (value ? `${prefix}${value}` : '');

// What the operator reads for one reconnection row. `ctx` is { record, poiState }:
// the record start is the reference of every check, and a source session is a
// problem (red) only once Optus has re-pointed. Times are Sydney time.
export function reconnectVerdict (row, ctx = {}) {
  const record = ctx.record || null;
  const nas = row.lastNasIp || '-';
  const oldIp = row.lastAcctIp || '-';
  const newIp = row.expectedIp || '-';
  const since = withText(' since ', sydneyTime(row.lastSessionStart));
  const verdict = (text, tone, offline) => ({ text, tone, offline });
  switch (row.reason) {
    case 'suspended':
      return verdict(SUSPENDED_ADVICE, 'grey', false);
    case 'notOnOurRadius':
      return verdict('Not on our RADIUS (L2)', 'grey', false);
    case 'noRadiusService': {
      const ids = row.ambiguousRadiusServiceIds || [];
      return ids.length > 1
        ? verdict(`Two RADIUS services (${ids.join(', ')}): fix in VISP`, 'grey', false)
        : verdict('No RADIUS service for this AVC', 'grey', false);
    }
    case 'reconnected':
      return verdict(`Online on ${nas}${since}`, 'green', false);
    case 'oldIpNotCommitted':
      return verdict(`On ${nas} with old IP ${oldIp}${since}. New IP ${newIp} is not committed yet.`, 'amber', true);
    case 'oldIpBeforeCommit':
      return verdict(`On ${nas} with old IP ${oldIp}${since}. ${newIp} applies when the session restarts.`, 'amber', true);
    case 'oldIpAfterCommit': {
      const committed = sydneyTime(row.committedAt || (record && record.ipsCommittedAt));
      return verdict(`On ${nas} with old IP ${oldIp} although ${newIp} was committed${withText(' at ', committed)}.`, 'red', true);
    }
    case 'sourceOnly':
      return verdict(`Still on ${nas}${since}`, record && record.repointConfirmedAt ? 'red' : 'grey', true);
    case 'ended': {
      const cause = withText(', ', row.lastTerminateCause);
      return verdict(`Offline${withText(' since ', sydneyTime(row.lastSessionStop))} (last on ${nas}${cause})`, 'red', true);
    }
    case 'stale':
      return verdict(`Session on ${nas} not updated${withText(' since ', sydneyTime(row.lastUpdate))}: treat as offline`, 'red', true);
    case 'noSession': {
      const start = sydneyTime(record && record.createdAt);
      return verdict(`No session since the migration started${start ? ` (${start})` : ''}`, 'red', true);
    }
    default:
      return row.reconnected
        ? verdict(`Online on ${nas}${since}`, 'green', false)
        : verdict(row.reason || 'Not reconnected', 'red', true);
  }
}

const VERDICT_ADVICE = {
  oldIpNotCommitted: { text: 'Commit statics, then kick', tone: 'amber' },
  oldIpBeforeCommit: { text: 'Kick this. If the kick fails, reset the service (see Kick)', tone: 'amber' },
  oldIpAfterCommit: { text: 'RADIUS still gives the old IP. Ask dev to check the commit for this service', tone: 'red' },
};

// The advice column: the three old-IP verdicts say what to do, every other row
// keeps the advice by minutes since the kick.
export function verdictAdvice (row, kickedAt, nowUtc) {
  const advice = VERDICT_ADVICE[row.reason];
  return advice ? { ...advice, minutes: minutesSince(kickedAt, nowUtc) } : reconnectAdvice(row, kickedAt, nowUtc);
}

// The latest session of a row: start, NAS and IP, then its end and cause, or
// the last accounting update while it is open.
export function sessionLine (row) {
  if (!row || !row.lastSessionStart) return '';
  let line = `Started ${sydneyTime(row.lastSessionStart)}`;
  if (row.lastNasIp) line += ` on ${row.lastNasIp}`;
  if (row.lastAcctIp) line += `, IP ${row.lastAcctIp}`;
  if (row.lastSessionStop) {
    line += `. Ended ${sydneyTime(row.lastSessionStop)}${row.lastTerminateCause ? ` (${row.lastTerminateCause})` : ''}`;
  } else if (row.lastUpdate) {
    line += `. Last update ${sydneyTime(row.lastUpdate)}`;
  }
  return line;
}

export function referenceLabel (reconnect, poiState) {
  if (!reconnect || !reconnect.referenceUtc) return '';
  return `Sessions counted from the start of this migration (${formatDual(reconnect.referenceUtc, poiState)})`;
}

// L2 and network-only services are not on our RADIUS and suspended ones have no
// session to expect: none of them is ever offline.
export function reconnectSummary (reconnect) {
  if (reconnect && !hasEvidence(reconnect)) {
    return { reconnected: 0, offline: 0, suspended: 0, notOnRadius: 0, monitored: 0, text: NOT_AVAILABLE_YET };
  }
  const rows = (reconnect && reconnect.rows) || [];
  const notOnRadius = rows.filter(r => NOT_ON_OUR_RADIUS.includes(r.reason)).length;
  const suspended = rows.filter(isSuspended).length;
  const monitored = rows.length - notOnRadius - suspended;
  const reconnected = rows.filter(r => !NOT_ON_OUR_RADIUS.includes(r.reason) && !isSuspended(r) && r.reconnected).length;
  const offline = monitored - reconnected;
  const suspendedText = suspended ? ` . ${suspended} suspended` : '';
  return {
    reconnected,
    offline,
    suspended,
    notOnRadius,
    monitored,
    text: reconnect ? `${reconnected} of ${monitored} back . ${offline} offline${suspendedText} . ${notOnRadius} L2 / not on our RADIUS` : '',
  };
}

// One line per service with a RADIUS service, offline ones first; a suspended one is listed but never offline.
export function reconnectTable ({ reconnect, rows, wholesalerNames, kickedAt, nowUtc, poiState, record, kickByService }) {
  const byAvc = indexBy(rows, 'avcId');
  const table = (hasEvidence(reconnect) ? reconnect.rows || [] : [])
    .filter(r => !NOT_ON_OUR_RADIUS.includes(r.reason))
    .map(r => {
      const advice = verdictAdvice(r, kickedAt, nowUtc);
      const verdict = reconnectVerdict(r, { record, poiState });
      const row = byAvc[r.avcId];
      return {
        avcId: r.avcId,
        radiusServiceId: r.radiusServiceId,
        user: r.radiusUsername,
        ipType: r.ipType,
        wholesaler: row ? wholesalerText(row, wholesalerNames) : '',
        minutes: advice.minutes,
        lastSession: r.lastSessionStart ? formatDual(r.lastSessionStart, poiState) : kickedAt ? 'none since kick' : 'none',
        nas: r.lastNasIp || '-',
        ip: r.lastAcctIp || '-',
        expectedIp: r.expectedIp,
        offline: verdict.offline,
        status: verdict.text,
        statusTone: verdict.tone,
        session: sessionLine(r),
        proof: kickByService && kickByService[kickKey(r)] ? kickProof(kickByService[kickKey(r)], r, nowUtc) : null,
        advice: advice.text,
        adviceTone: advice.tone,
        reason: r.reason,
      };
    });
  return table.filter(r => r.offline).concat(table.filter(r => !r.offline));
}

// "2026-10-14_1112": the Sydney date and time in a CSV file name.
function csvStamp (nowUtc) {
  const [day, time] = formatInZone(nowUtc, SYDNEY, { year: true }).split(' ');
  const [dd, mm, yyyy] = day.split('/');
  return `${yyyy}-${mm}-${dd}_${time.replace(':', '')}`;
}

// The list for the wholesalers: services still offline.
export function toOfflineCsv (table, { cvcName, nowUtc }) {
  const columns = [
    { header: 'Wholesaler', value: 'wholesaler' },
    { header: 'AVC', value: 'avcId' },
    { header: 'Username', value: 'user' },
    { header: 'IP type', value: 'ipType' },
    { header: 'Expected IP', value: 'expectedIp' },
    { header: 'Minutes offline', value: 'minutes' },
  ];
  const name = String(cvcName).replace(/[\\/:*?"<>|]/g, '-');
  return {
    filename: `${name}_offline_${csvStamp(nowUtc)}_SYD.csv`,
    text: toCsv(columns, (table || []).filter(r => r.offline)),
  };
}

// ---- Section D: commit and mark migrated ----

const staticRow = (r) => isVisp(r) && r.ipType === 'Static';
const toCommit = (rows) => (rows || []).filter(r => staticRow(r) && r.radiusServiceId && r.hasPendingIp && !r.committed);

// "4 to commit . 0 done . 0 failed"; failures come from the last commit answer
// of this tab or, after a reload, from the Usher ledger (`ledgerFailed`).
export function commitSummary (rows, lastBatch, ledgerFailed = 0) {
  const pending = toCommit(rows).length;
  const done = (rows || []).filter(r => staticRow(r) && r.committed).length;
  const failed = Math.max((lastBatch && lastBatch.failed) || 0, ledgerFailed || 0);
  return { pending, done, failed, text: `${pending} to commit . ${done} done . ${failed} failed` };
}

const staticsText = (n) => `${n} ${plural(n, 'static', 'statics')}`;

// Before the confirm the source is shut and the statics get their new IP when
// they reconnect through the target; after the confirm the session has to restart.
export function commitDialog (rows, preflight, reconnect) {
  const lines = toCommit(rows);
  const record = preflight && preflight.record;
  const head = `Write ${lines.length} new static IPs to RADIUS (user old -> new, one per line below). `;
  const body = record && !record.repointConfirmedAt
    ? `${head}${preflight.sourceHostname} is shut, so each static gets its new IP when it reconnects through `
      + `${preflight.targetHostname} after the Optus re-point. Old IPs are quarantined for 7 days. Rollback (section R) puts them back.`
    : `${head}Old IPs are quarantined for 7 days. Customers keep their old IP until their session restarts (Kick).`;
  const online = commitOnlineLine(reconnect);
  return {
    title: 'Commit static IPs',
    body: [body],
    consequences: lines.map(r => `${r.radiusUsername} ${r.currentIp} -> ${r.pendingIp}`),
    alerts: online ? [{ type: 'warning', text: online }] : [],
    proceedLabel: 'Commit',
    requireTyping: false,
  };
}

const ledgerStatics = (preflight) => (preflight && preflight.rollback && preflight.rollback.statics) || [];
// A static whose RADIUS IP is the new one; when RADIUS could not be read the
// commit stamp stands in unless a restore came after it.
const ledgerCommitted = (s) => s.state === 'committed'
  || (s.state === 'unknown' && !!s.committedAt && !(s.restoredAt && timeMs(s.restoredAt) >= timeMs(s.committedAt)));
// Not on the new IP and the last outcome is a commit failure newer than its commit and restore
// stamps: the latest attempt failed, also when an earlier window committed it and a rollback
// restored it. A restore failure ("Not restored: ...") is not a failed commit.
const ledgerFailed = (s) => !ledgerCommitted(s) && !!s.lastOutcome && !/^not restored/i.test(s.lastOutcome)
  && timeMs(s.lastOutcomeAt) > Math.max(timeMs(s.committedAt), timeMs(s.restoredAt));

// What the last commit left, read from the Usher ledger so it survives a reload:
// green when every static took its new IP, red with the failed count otherwise.
export function commitResultLine (preflight) {
  const rows = ledgerStatics(preflight);
  const done = rows.filter(ledgerCommitted);
  const failed = rows.filter(ledgerFailed);
  if (!done.length && !failed.length) return null;
  const stamps = done.map(s => s.committedAt).filter(Boolean);
  const times = stamps.length ? stamps : failed.map(s => s.lastOutcomeAt).filter(Boolean);
  const newest = times.reduce((a, b) => (timeMs(b) > timeMs(a) ? b : a), times[0]);
  const total = done.length + failed.length;
  const when = `Statics committed ${formatDual(newest, preflight.poiState)}: ${done.length} of ${total}`;
  return failed.length > 0
    ? {
      type: 'error',
      text: `${when}, ${failed.length} failed. Retry failed, or these come back with their old IP and need Kick all after activation.`,
      done: done.length,
      failed: failed.length,
    }
    : { type: 'success', text: `${when}. Rollback (section R) puts the old IPs back.`, done: done.length, failed: 0 };
}

// Under step 1 once the source shut is complete: commit now, before Optus re-points
// (unless sessions are already on the target).
export function commitNextLine (preflight, reconnect) {
  const steps = preflight && preflight.steps;
  if (!steps || steps.phase !== 'waitingRepoint') return '';
  const shut = newestEvent(preflight.record, [SHUT_TYPE]);
  if (!shut || statusOf(shut) !== 'complete') return '';
  const uncommitted = toCommit(preflight.rows).length;
  if (!uncommitted) return '';
  const seen = hasEvidence(reconnect) && monitoredOnTarget(reconnect) > 0;
  return `Next: Commit ${staticsText(uncommitted)} now${seen ? '' : ', before Optus re-points'}. `
    + `Each static then gets its new IP when it reconnects through ${preflight.targetHostname}.`;
}

// A static that reconnected through the target before its commit holds its old
// IP until its session restarts.
const ONLINE_OLD_IP_REASONS = ['oldIpNotCommitted', 'oldIpBeforeCommit'];

export function commitOnlineLine (reconnect) {
  if (!hasEvidence(reconnect)) return '';
  const online = (reconnect.rows || []).filter(r => r.ipType === 'Static' && ONLINE_OLD_IP_REASONS.includes(r.reason)).length;
  if (!online) return '';
  const nas = reconnect.targetNasIp;
  return online === 1
    ? `1 static is already online on ${nas} with its old IP. It gets the new IP when its session restarts (Kick all after activation).`
    : `${online} statics are already online on ${nas} with their old IP. `
      + 'They get the new IP when their session restarts (Kick all after activation).';
}

// Section D no longer holds the commit: it points at step 1 of section C.
export function commitPointerLine (rows) {
  const pending = toCommit(rows).length;
  return pending > 0 ? `Commit statics: section C step 1 (${pending} to commit)` : '';
}

// The summary before "Mark migrated" (it closes Build and Rebuild for this CVC).
export function markMigratedText (reconnect, record) {
  const summary = reconnectSummary(reconnect);
  let back = 'Reconnected not checked';
  if (hasEvidence(reconnect)) {
    back = `Reconnected ${summary.reconnected}/${summary.monitored}, offline ${summary.offline}`;
    if (summary.suspended) back += `, suspended ${summary.suspended}`;
  }
  else if (reconnect) back = 'Reconnected not available yet';
  const unshut = unshutCopies(record).length;
  const orphans = ((record && record.targetCopies) || []).filter(c => c.orphan && !c.removedAt).length;
  return `${back}, unshut source copies ${unshut}, orphan copies ${orphans}. `
    + 'After this, Build and Rebuild are closed for this CVC; later breakages go through VISP suspend and resume. '
    + 'Verify, Kick and Remove source copies stay available.';
}

// ---- Section E: clean-up and cancel ----

export const unremoved = (copies) => (copies || []).filter(c => !c.removedAt);

// "Source copies on <host>: 6 shut . 0 removed . Removable from 21/10 10:32 SYD . 07:32 PER"
export function sourceCopiesSummary (preflight) {
  const record = preflight.record || {};
  const copies = record.sourceCopies || [];
  const shut = unremoved(copies).filter(c => c.shutAt).length;
  const removed = copies.length - unremoved(copies).length;
  const notShut = unshutCopies(record).length;
  const from = record.sourceRemovableFrom
    ? `Removable from ${formatDual(record.sourceRemovableFrom, preflight.poiState)}`
    : 'Removable from: after activation';
  return `Source copies on ${preflight.sourceHostname}: ${shut} shut . ${removed} removed`
    + `${notShut > 0 ? ` . ${notShut} not shut` : ''} . ${from}`;
}

export function sourceCopyRows (record, poiState) {
  const time = (utc) => (utc ? formatDual(utc, poiState) : '-');
  return ((record && record.sourceCopies) || []).map(c => ({
    id: c.id,
    interfaceName: c.interfaceName,
    bridgeDomain: c.bridgeDomain,
    shutAtText: time(c.shutAt),
    removedAtText: time(c.removedAt),
  }));
}

export function removeSourceDialog (preflight) {
  const cvc = preflight.cvcName;
  const copies = unremoved((preflight.record || {}).sourceCopies);
  return {
    title: `Remove the source copies of ${cvc}`,
    body: [
      `Remove ${copies.length} shut subinterfaces of ${cvc} from ${preflight.sourceHostname} (list). `
      + `Nothing changes on ${preflight.targetHostname} or in Usher AVCs.`,
      'After this, the migration cannot be rolled back from this page.',
    ],
    consequences: copies.map(c => `${c.interfaceName} (${c.bridgeDomain})`),
    extra: { label: 'Agreed by', hint: 'e.g. Name', maxlength: 50, required: true },
    proceedLabel: `Remove from ${preflight.sourceHostname}`,
    requireTyping: true,
  };
}

// The cancel wizard: the release box starts ticked, because a cancelled CVC
// rarely needs its IPs held.
export function cancelDialog (preflight) {
  const cvc = preflight.cvcName;
  const state = preflight.poiState;
  const targetCopyIds = unremoved((preflight.record || {}).targetCopies).map(c => c.id);
  const reserved = reservedRows(preflight);
  return {
    title: `Cancel the migration of ${cvc}`,
    body: [
      `Cancel the migration of ${cvc}. Steps: 1 close the record, 2 remove ${targetCopyIds.length} target copies from ${preflight.targetHostname}, `
      + `3 release ${reserved} reserved IPs (leave them reserved if this CVC is rebooked soon; `
      + `released ${state} IPs can be taken by other ${state} static requests).`,
    ],
    checkbox: { label: `Release ${reserved} reserved IPs`, shown: reserved > 0, checked: true },
    proceedLabel: 'Cancel migration',
    requireTyping: true,
    targetCopyIds,
    reserved,
  };
}

// Close the record, then remove the target copies, then release the IPs. A step
// with nothing to do is left out.
export function planCancel ({ targetCopyIds, reserved, release }) {
  const plan = [{ key: 'cancel', label: 'Close the record' }];
  if ((targetCopyIds || []).length) {
    plan.push({ key: 'remove', label: `Remove ${targetCopyIds.length} target copies`, copyIds: targetCopyIds });
  }
  if (release && reserved > 0) plan.push({ key: 'release', label: `Release ${reserved} reserved IPs` });
  return plan;
}

// Runs the steps one after the other and stops at the first failure, naming it. The
// words are the cancel wizard's; the rollback wizard that closes a record passes its own.
export async function runCancelWizard ({ plan, runStep, stopped = 'Cancel stopped', done = 'Cancelled' }) {
  const results = {};
  for (let i = 0; i < plan.length; i++) {
    try {
      results[plan[i].key] = await runStep(plan[i]);
    } catch (e) {
      const message = errorMessage(e);
      return {
        failed: { key: plan[i].key, message },
        text: `${stopped} at step ${i + 1} of ${plan.length} (${plan[i].label}): ${message}`,
      };
    }
  }
  const remove = plan.find(s => s.key === 'remove');
  const released = results.release && typeof results.release.done === 'number' ? results.release.done : 0;
  return {
    failed: null,
    text: `${done}; ${remove ? remove.copyIds.length : 0} copy removals queued; ${released} IPs released`,
  };
}

// The cancel wizard closes the record before it releases the IPs, so a failed
// release is retried on its own. The server's canRelease says whether it can
// be (Release stays open on a cancelled record while IPs are still reserved).
export function releaseRetry (cancelResult, steps) {
  if (!cancelResult || !cancelResult.failed || cancelResult.failed.key !== 'release') return null;
  return Object.assign({ label: 'Retry release' }, stepButton(steps, 'canRelease'));
}

// The release step again, on its own. Same result shape as the wizard.
export async function runReleaseRetry ({ runStep }) {
  try {
    const answer = await runStep();
    const released = answer && typeof answer.done === 'number' ? answer.done : 0;
    return { failed: null, text: `Released ${released} reserved IPs` };
  } catch (e) {
    const message = errorMessage(e);
    return { failed: { key: 'release', message }, text: `Release failed: ${message}` };
  }
}
